import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Download, Eye, Info, AlertTriangle, UserSearch, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/common/DataTable";
import { CeldaAltasMes } from "@/components/admin/CeldaAltasMes";
import { AjustarAltasButton } from "@/components/admin/AjustarAltasButton";
import { MoneyInput } from "@/components/common/MoneyInput";
import { ComboSelect } from "@/components/common/ComboSelect";
import { OrigenClienteSelect } from "@/components/common/OrigenClienteSelect";
import { AnclaPagoSelect } from "@/components/common/AnclaPagoSelect";
import { SicAutorizacionProspecto } from "@/components/common/SicAutorizacionProspecto";
import { DocsIdentidadCliente } from "@/components/common/DocsIdentidadCliente";
import { TablaAmortizacionCredito } from "@/components/creditos/TablaAmortizacionCredito";
import { TablaRentasCredito } from "@/components/creditos/TablaArrendamiento";
import { GeoPuntoOpcional } from "@/components/common/GeoPuntoOpcional";
import { ExportExcelButton } from "@/components/common/ExportExcelButton";
import { ActividadOtroBuscador } from "@/components/common/ActividadOtroBuscador";
import { LocalidadBuscador } from "@/components/common/LocalidadBuscador";
import { api, listResource, createResource, updateResource, deleteResource, apiError } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { sugeridoDePago, desgloseOrdenado } from "@/lib/sugeridoPago";
import { useAuth } from "@/context/AuthContext";
import { esActividadVulnerable } from "@/lib/actividad_vulnerable";
import { EliminarEntidadDialog } from "@/components/admin/EliminarEntidadDialog";
import { AccionesSolicitud } from "@/components/solicitudes/AccionesSolicitud";
import { camposSelladosDe, estaSellada, estaCancelada, etiquetaEstatus, estatusEfectivo } from "@/lib/selloSolicitud";
import { esFactoraje, esCreditoFactoraje, TIPO_FACTORAJE } from "@/lib/factoraje";
import { PagadorFactorajeSelect } from "@/components/creditos/PagadorFactorajeSelect";
import { useFactorajeHabilitado } from "@/hooks/useFactorajeHabilitado";
import { FactorajeCaptura } from "@/components/solicitudes/FactorajeCaptura";
import { esArrendamiento, esCreditoArrendamiento, TITULO_RENTAS } from "@/lib/arrendamiento";
import { useArrendamientoHabilitado } from "@/hooks/useArrendamientoHabilitado";
import { ArrendamientoCaptura } from "@/components/solicitudes/ArrendamientoCaptura";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ScreeningResultadoDialog } from "@/components/prospectos/ScreeningResultadoDialog";
import { ActividadEconomicaCombobox } from "@/components/common/ActividadEconomicaCombobox";
import { ESTADOS_MX, ACTIVIDADES_ECONOMICAS, ACTIVIDADES_GRUPOS, PLAZOS_PERMITIDOS_RANGO } from "@/lib/catalogos_maestros";
import { formatearPlazo, etiquetaPeriodicidad } from "@/lib/format";
import { TP_PF } from "@/lib/nav";

// Set derivado para lookup O(1) del tipo_persona PF en el field `nombre_con_desglose`.
const TP_PF_SET = new Set(TP_PF);

/** Multiselect numérico del rango 4..60 (PLAZOS_PERMITIDOS_RANGO). Guarda el subconjunto
 *  elegido como lista de enteros ordenada. Rompe con el legacy string CSV. */
function PlazosMultiControl({ field, value, id, onChange }) {
  const seleccionados = Array.isArray(value)
    ? value.filter((n) => Number.isFinite(Number(n))).map(Number)
    : (typeof value === "string" && value.trim()
        ? value.split(",").map((x) => Number(x.trim())).filter((n) => Number.isFinite(n))
        : []);
  const toggle = (n) => {
    const set = new Set(seleccionados);
    if (set.has(n)) set.delete(n); else set.add(n);
    onChange(field.key, Array.from(set).sort((a, b) => a - b));
  };
  const seleccionarTodos = () => onChange(field.key, [...PLAZOS_PERMITIDOS_RANGO]);
  const limpiar = () => onChange(field.key, []);
  const maxSel = seleccionados.length ? Math.max(...seleccionados) : null;
  return (
    <div id={id} data-testid={`plazos-multi-${field.key}`} className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-600">
        <span>
          <b>{seleccionados.length}</b>/{PLAZOS_PERMITIDOS_RANGO.length} plazos seleccionados{" "}
          {maxSel != null && <span className="text-slate-500">· máx: <b>{maxSel}</b> pagos</span>}
        </span>
        <div className="flex gap-1.5">
          <button type="button" onClick={seleccionarTodos} data-testid={`plazos-multi-${field.key}-all`}
            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] hover:bg-slate-100">
            Todos
          </button>
          <button type="button" onClick={limpiar} data-testid={`plazos-multi-${field.key}-clear`}
            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] hover:bg-slate-100">
            Ninguno
          </button>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8 lg:grid-cols-10">
        {PLAZOS_PERMITIDOS_RANGO.map((n) => {
          const on = seleccionados.includes(n);
          return (
            <button
              type="button"
              key={n}
              onClick={() => toggle(n)}
              data-testid={`plazo-chip-${n}`}
              className={
                "rounded-md border px-2 py-1 text-xs tabular-nums transition-colors " +
                (on
                  ? "border-primary bg-primary/10 font-semibold text-primary"
                  : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100")
              }
              aria-pressed={on}
            >
              {n}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Plazo al capturar solicitud/crédito: si el producto tiene lista de plazos_permitidos,
 *  se ofrece como Select LIMITADO a esa lista; si no, cae a input numérico con range 4..60. */
function PlazoFromProductoControl({ field, value, id, onChange, producto }) {
  const periodicidad = producto?.periodicidad || "";
  const hint = periodicidad && (
    <p className="mt-1 text-[11px] text-slate-500" data-testid={`plazo-periodicidad-hint-${field.key}`}>
      Plazo — <span className="font-medium">{etiquetaPeriodicidad(periodicidad)}</span> (producto {periodicidad.toLowerCase()})
    </p>
  );
  const opciones = Array.isArray(producto?.plazos_permitidos)
    ? producto.plazos_permitidos.filter((n) => Number.isFinite(Number(n))).map(Number).sort((a, b) => a - b)
    : [];
  if (opciones.length > 0) {
    return (
      <div>
        <Select value={value != null && value !== "" ? String(value) : ""}
          onValueChange={(v) => onChange(field.key, Number(v))}>
          <SelectTrigger id={id} data-testid={`select-${field.key}`}>
            <SelectValue placeholder={`Elige uno de ${opciones.length} plazos permitidos...`} />
          </SelectTrigger>
          <SelectContent>
            {opciones.map((n) => (
              <SelectItem key={n} value={String(n)}>{periodicidad ? formatearPlazo(n, periodicidad) : `${n} pagos`}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hint}
      </div>
    );
  }
  // Fallback: producto sin lista (legacy) → input libre con rango 4..60 en HTML.
  return (
    <div>
      <Input id={id} data-testid={`input-${field.key}`} type="number" min="4" max="60"
        value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value ? Number(e.target.value) : "")} />
      {hint}
    </div>
  );
}

// (jul-2026) Resumen en lenguaje llano del producto de crédito. Actualizado en vivo
// conforme el operador captura. Es SOLO presentación: no persiste nada, no calcula
// campos que se envíen. El ejemplo sobre $100,000 sirve como red de seguridad visual
// para detectar 500% mal capturado antes de guardar.
function ResumenProductoCredito({ form = {} }) {
  const fmtMxn = (n) => `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const comisionTipo = String(form.comision_apertura_tipo || "").toLowerCase();
  const comision = Number(form.comision_apertura ?? 0);
  const comisionCobro = String(form.comision_cobro_modo || "descontada");
  const gcTipo = String(form.gastos_cobranza_tipo || "").toLowerCase();
  const gc = Number(form.gastos_cobranza ?? 0);
  const gcDisp = Math.max(1, Number(form.gc_disparo_dias || 1));
  const gcEtq = form.gc_etiqueta || "Gastos de cobranza";
  const iva = String(form.iva ?? "");
  const tasaMor = Number(form.tasa_moratoria ?? 0);
  const baseMor = String(form.base_moratorio || "capital");
  const baseDias = String(form.base_dias_mora || "360");
  const tasaOrd = Number(form.tasa_ordinaria ?? 0);
  // BUILD 1 rangos (feb-2026): tasas y monto pueden venir como rango. Se lee el par
  // min/max si existe, con fallback al campo único legado. Formato "X% – Y%" o "X%"
  // cuando min = max.
  const _rangoTxt = (mn, mx, sufijo = "%") => {
    if (mn == null || mn === "") return null;
    if (mx == null || mx === "" || Number(mn) === Number(mx)) return `${mn}${sufijo}`;
    return `${mn}${sufijo} a ${mx}${sufijo}`;
  };
  const tasaOrdRango = _rangoTxt(form.tasa_ordinaria_min ?? form.tasa_ordinaria, form.tasa_ordinaria_max ?? form.tasa_ordinaria);
  const tasaMorRango = _rangoTxt(form.tasa_moratoria_min ?? form.tasa_moratoria, form.tasa_moratoria_max ?? form.tasa_moratoria);
  const _fmtMoney = (n) => n != null && n !== "" ? fmtMxn(n) : null;
  const montoMnFmt = _fmtMoney(form.monto_min);
  const montoMxFmt = _fmtMoney(form.monto_max);
  const montoRango = (montoMnFmt && montoMxFmt)
    ? (form.monto_min === form.monto_max ? montoMnFmt : `${montoMnFmt} a ${montoMxFmt}`)
    : null;
  const nombre = String(form.nombre || "").trim();

  // Detectar campos faltantes en lenguaje llano
  const falta = [];
  if (!nombre) falta.push("Nombre del producto");
  if (!form.tipo) falta.push("Tipo");
  if (!form.periodicidad) falta.push("Periodicidad");
  // BUILD 1 rangos: acepta cualquiera de los dos formatos (rango o único legado).
  const _tieneOrd = (form.tasa_ordinaria_min != null && form.tasa_ordinaria_min !== "")
                    || (form.tasa_ordinaria != null && form.tasa_ordinaria !== "");
  const _tieneMor = (form.tasa_moratoria_min != null && form.tasa_moratoria_min !== "")
                    || (form.tasa_moratoria != null && form.tasa_moratoria !== "");
  if (!_tieneOrd) falta.push("Tasa ordinaria (mínima)");
  if (!_tieneMor) falta.push("Tasa moratoria (mínima)");
  if (!comisionTipo) falta.push("Tipo de comisión por apertura");
  if (form.comision_apertura == null || form.comision_apertura === "") falta.push("Comisión por apertura (captura 0 si no cobras)");
  if (!form.comision_cobro_modo) falta.push("Forma de cobro de la comisión");
  if (!iva) falta.push("IVA");
  if (!gcTipo) falta.push("Tipo de gastos de cobranza");
  if (form.gastos_cobranza == null || form.gastos_cobranza === "") falta.push("Gastos de cobranza (captura 0 si no cobras)");
  if (!form.gc_etiqueta) falta.push("Etiqueta del cargo");
  if (!baseDias) falta.push("Base de días para moratorios");
  if (!baseMor) falta.push("Base del interés moratorio");
  if (!form.politica_remanente) falta.push("Política de remanente");
  if (!form.tipo_amortizacion) falta.push("Sistema de amortización");
  if (!Array.isArray(form.plazos_permitidos) || form.plazos_permitidos.length === 0) falta.push("Plazos permitidos");

  const comisionCobroTxt = {
    descontada: "se descuenta del desembolso — el cliente recibe el neto",
    primera_cuota: "se cobra íntegra en la primera cuota",
    diferida: "se prorratea en todas las cuotas",
  }[comisionCobro] || "—";

  const baseMorTxt = baseMor === "capital_interes"
    ? "capital + interés vencido (con pacto expreso)"
    : "capital vencido";
  const ivaTxt = iva === "0" ? "exento (0%)" : `${iva}%`;

  // Comisión en llano + ejemplo
  let comisionLinea = null, comisionEjemplo = null;
  if (comisionTipo && comision >= 0) {
    if (comisionTipo === "monto") {
      comisionLinea = `${fmtMxn(comision)} (monto fijo)`;
    } else {
      comisionLinea = `${comision}% del monto autorizado`;
      const base = 100000;
      const calc = base * comision / 100;
      comisionEjemplo = `en un crédito de ${fmtMxn(base)} la comisión sería ${fmtMxn(calc)} más IVA.`;
    }
  }

  // GC en llano + ejemplo
  let gcLinea = null, gcEjemplo = null;
  if (gcTipo && gc >= 0) {
    if (gc === 0) {
      gcLinea = "no se cobra";
    } else if (gcTipo === "monto") {
      gcLinea = `${fmtMxn(gc)} por gestión`;
    } else {
      gcLinea = `${gc}% del capital vencido`;
      const base = 100000;
      const calc = base * gc / 100;
      gcEjemplo = `si vence una cuota con ${fmtMxn(base)} de capital, el cargo sería ${fmtMxn(calc)} más IVA.`;
    }
  }

  return (
    <div data-testid="resumen-producto-credito"
      className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-slate-800 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">Resumen del producto en lenguaje llano</p>
      <ul className="space-y-1.5">
        <li data-testid="resumen-comision">
          <b>Comisión de apertura:</b>{" "}
          {comisionLinea || <span className="text-slate-400">falta configurar tipo o valor</span>}
          {comisionCobroTxt && comisionLinea ? <> — {comisionCobroTxt}.</> : "."}
          {comisionEjemplo && (
            <div className="text-[11.5px] text-primary/80 mt-0.5" data-testid="resumen-comision-ejemplo">
              <b>Ejemplo:</b> {comisionEjemplo}
            </div>
          )}
        </li>
        <li data-testid="resumen-gc">
          <b>Gastos de cobranza:</b>{" "}
          {gcLinea || <span className="text-slate-400">falta configurar tipo o valor</span>}
          {gc > 0 && <> — se aplica automáticamente a partir del día <b>{gcDisp}</b> de atraso.</>}
          {gc > 0 && <> Aparecerá como: <b>{gcEtq}</b>.</>}
          {gcEjemplo && (
            <div className="text-[11.5px] text-primary/80 mt-0.5" data-testid="resumen-gc-ejemplo">
              <b>Ejemplo:</b> {gcEjemplo}
            </div>
          )}
        </li>
        <li data-testid="resumen-iva">
          <b>IVA de intereses y comisiones:</b> {iva ? ivaTxt : <span className="text-slate-400">falta</span>}. <b>IVA del cargo por atraso:</b> siempre 16%.
        </li>
        <li data-testid="resumen-mora">
          <b>Interés moratorio:</b>{" "}
          {tasaMorRango
            ? <>{tasaMorRango} anual sobre {baseMorTxt} — base {baseDias} días.</>
            : <span className="text-slate-400">falta configurar tasa moratoria.</span>}
        </li>
        <li data-testid="resumen-ordinario">
          <b>Interés ordinario:</b>{" "}
          {tasaOrdRango
            ? <>{tasaOrdRango} anual sobre saldo insoluto.</>
            : <span className="text-slate-400">falta configurar tasa ordinaria.</span>}
        </li>
        {montoRango && (
          <li data-testid="resumen-monto">
            <b>Monto:</b> {montoRango}. El monto capturado en la solicitud debe caer dentro de este rango.
          </li>
        )}
      </ul>
      {falta.length > 0 && (
        <div data-testid="resumen-faltantes" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <b>Falta configurar:</b> {falta.join(", ")}
        </div>
      )}
    </div>
  );
}

function FieldControl({ field, value, form = {}, onChange, productos = [], onProducto, clientes = [], onCliente, creditos = [], onCredito, onCreditoQuery, paises = [] }) {
  const id = `field-${field.key}`;
  // Producto de interés (jul-2026): catálogo de productos de crédito ACTIVOS de la entidad.
  // Guarda el NOMBRE (string) para no romper la columna Producto ni el CSV. Si el registro
  // trae un valor histórico que ya no existe en el catálogo, se ofrece como opción marcada
  // "(valor histórico)" para NUNCA perder el dato ni forzar el cambio.
  if (field.type === "producto_nombre_select") {
    const nombres = (productos || [])
      .filter((p) => p.activo !== false)
      .map((p) => p.nombre)
      .filter(Boolean);
    const historico = value && !nombres.includes(value) ? value : null;
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder="Seleccionar producto..." />
        </SelectTrigger>
        <SelectContent>
          {historico && (
            <SelectItem value={historico} data-testid={`opt-${field.key}-historico`}>
              {historico} (valor histórico)
            </SelectItem>
          )}
          {nombres.map((n) => (
            <SelectItem key={n} value={n} data-testid={`opt-${field.key}-${n}`}>{n}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  // Cambio 2: entidad_federativa como select cerrado de 32 estados MX (INEGI).
  if (field.type === "estado_mx_select")
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder="Seleccionar estado..." />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {ESTADOS_MX.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  // Select cerrado de nacionalidades (~200 países ISO 3166-1). Al elegir persiste el gentilicio
  // legible en `field.key` y el ISO2 en `<field.key>_iso` (espejo para el cruce por código del EBR).
  // El operador NO ve marca de riesgo: la lista GAFI queda opaca en el backend.
  // Buscable (oct-2026): usa ComboSelect con filtro normalizado (sin acentos) para que "mex"
  // encuentre "México"/"Mexicana"; México se ordena primero en la lista.
  if (field.type === "nacionalidad_select" || field.type === "pais_select") {
    const esNac = field.type === "nacionalidad_select";
    const lista = [...paises].sort((a, b) => (a.iso2 === "MX" ? -1 : b.iso2 === "MX" ? 1 : 0));
    return (
      <ComboSelect
        testid={`select-${field.key}`}
        items={lista}
        value={value ?? ""}
        getKey={(p) => (esNac ? p.nacionalidad : p.nombre)}
        getLabel={(p) => (esNac ? p.nacionalidad : p.nombre)}
        getSearch={(p) => `${p.nombre} ${p.nacionalidad} ${p.iso2}`}
        onSelect={(p) => { onChange(field.key, esNac ? p.nacionalidad : p.nombre); onChange(`${field.key}_iso`, p.iso2 || ""); }}
        placeholder={esNac ? "Seleccionar nacionalidad..." : "Seleccionar país..."}
        searchPlaceholder="Escribe para buscar..."
        emptyText={paises.length === 0 ? "Cargando catálogo…" : "Sin resultados."}
      />
    );
  }
  // Solicitud: la periodicidad está AMARRADA al producto (jul-2026). Se muestra como badge
  // informativo derivado de `producto_id`; el operador ya no puede modificarla. Al backend
  // igualmente la periodicidad final se toma de producto.periodicidad (defensa en profundidad).
  if (field.type === "producto_periodicidad_readonly") {
    const prodId = form.producto_id;
    const prod = productos.find((p) => p.id === prodId);
    const per = prod?.periodicidad || value || "";
    return (
      <div className="flex items-center gap-2">
        <div id={id} data-testid={`badge-${field.key}`}
          className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700 min-w-[8rem]">
          {per || <span className="text-slate-400">— (elige producto primero)</span>}
        </div>
        <p className="text-[11px] text-slate-500">Se hereda del producto y no se puede cambiar.</p>
      </div>
    );
  }
  // (jul-2026 Fix H1) Comisión por apertura en la SOLICITUD: label dinámico según
  // BUILD 1 rangos (feb-2026) — tasa con rango del producto seleccionado. Muestra el
  // rango permitido debajo del input y valida en front (badge de error) que el valor
  // esté dentro. El backend valida de nuevo con HTTP 400 (defensa en profundidad).
  if (field.type === "tasa_con_rango_producto") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    const minKey = field.rangoMinKey || `${field.key}_min`;
    const maxKey = field.rangoMaxKey || `${field.key}_max`;
    const mn = prod?.[minKey];
    const mx = prod?.[maxKey];
    const tieneRango = mn != null && mx != null && mn !== "" && mx !== "";
    const numVal = value === "" || value == null ? null : Number(value);
    const fueraDeRango = tieneRango && numVal != null && (numVal < Number(mn) || numVal > Number(mx));
    return (
      <div>
        <Input id={id} data-testid={`input-${field.key}`} type="number" step="0.01"
          value={value ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={fueraDeRango ? "border-red-400 focus:ring-red-400" : ""} />
        {tieneRango ? (
          <p className={`mt-1 text-[11px] ${fueraDeRango ? "text-red-600 font-medium" : "text-slate-500"}`}
            data-testid={`hint-${field.key}`}>
            Rango permitido del producto: {mn}% – {mx}% anual.
            {fueraDeRango && ` El valor ${numVal}% está fuera del rango.`}
          </p>
        ) : prod ? (
          <p className="mt-1 text-[11px] text-slate-400" data-testid={`hint-${field.key}`}>
            Este producto no declara rango — la tasa es libre.
          </p>
        ) : (
          <p className="mt-1 text-[11px] text-slate-400" data-testid={`hint-${field.key}`}>
            Selecciona un producto para conocer el rango permitido.
          </p>
        )}
      </div>
    );
  }
  // BUILD 1 rangos (feb-2026) — monto con rango del producto. Mismo patrón que tasa.
  if (field.type === "monto_con_rango_producto") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    const mn = prod?.monto_min;
    const mx = prod?.monto_max;
    const tieneRango = mn != null && mx != null && mn !== "" && mx !== "";
    const numVal = value === "" || value == null ? null : Number(value);
    const fueraDeRango = tieneRango && numVal != null && (numVal < Number(mn) || numVal > Number(mx));
    const fmt = (n) => Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
    return (
      <div>
        <Input id={id} data-testid={`input-${field.key}`} type="number" step="0.01"
          value={value ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          className={fueraDeRango ? "border-red-400 focus:ring-red-400" : ""} />
        {tieneRango ? (
          <p className={`mt-1 text-[11px] ${fueraDeRango ? "text-red-600 font-medium" : "text-slate-500"}`}
            data-testid={`hint-${field.key}`}>
            Rango permitido del producto: {fmt(mn)} – {fmt(mx)}.
            {fueraDeRango && ` El monto ${fmt(numVal)} está fuera del rango.`}
          </p>
        ) : prod ? (
          <p className="mt-1 text-[11px] text-slate-400" data-testid={`hint-${field.key}`}>
            Este producto no declara rango de monto — el monto es libre.
          </p>
        ) : null}
      </div>
    );
  }
  // producto.comision_apertura_tipo, deshabilitado si no hay producto o el producto no
  // tiene tipo definido. El valor se precarga en onProducto y sigue editable.
  if (field.type === "comision_apertura_solicitud") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    const tipoProd = String(prod?.comision_apertura_tipo || "").toLowerCase();
    const sufijo = tipoProd === "monto" ? "($)" : tipoProd === "porcentaje" ? "(%)" : "";
    const disabled = !prod || !tipoProd;
    return (
      <div>
        <Input id={id} data-testid={`input-${field.key}`} type="number" step="0.01"
          disabled={disabled}
          value={value ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          placeholder={disabled ? "" : (tipoProd === "monto" ? "0.00" : "0")}
          className={disabled ? "bg-slate-50 text-slate-400" : ""} />
        <p className="mt-1 text-[11px] text-slate-500" data-testid={`hint-${field.key}`}>
          {disabled
            ? "Selecciona un producto para capturar la comisión."
            : tipoProd === "monto"
              ? "Monto fijo en pesos. Se sella igual que en el producto (no se convierte a %)."
              : "Porcentaje sobre el monto autorizado. Ejemplo: 5 significa 5%."}
          {sufijo && <span className="ml-1 font-medium text-slate-600">{`Se guarda como ${sufijo}`}</span>}
        </p>
      </div>
    );
  }
  // (jul-2026 Fix H2) Tipo de comisión heredado del producto — badge readonly. NO viaja
  // en el payload (se excluye en save()); el backend hereda el tipo desde el producto.
  if (field.type === "comision_apertura_tipo_readonly") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    const tipoProd = String(prod?.comision_apertura_tipo || "").toLowerCase();
    const texto = tipoProd === "monto"
      ? "Monto fijo ($) — se hereda del producto y no se puede cambiar"
      : tipoProd === "porcentaje"
        ? "Porcentaje (%) sobre el monto autorizado — se hereda del producto y no se puede cambiar"
        : "— (elige producto primero)";
    const tone = tipoProd ? "bg-slate-50 text-slate-700 border-slate-200"
                          : "bg-slate-100 text-slate-400 border-slate-200";
    return (
      <div id={id} data-testid={`badge-${field.key}`}
        className={`inline-flex items-center rounded-md border px-3 py-1.5 text-sm font-medium ${tone}`}>
        {texto}
      </div>
    );
  }
  // (jul-2026 Fix H3) Bloque informativo de gastos de cobranza que el producto va a
  // sellar. Solo lectura, NO viaja en el payload. Se sella del producto en backend.
  if (field.type === "gastos_cobranza_readonly") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    if (!prod) {
      return (
        <div id={id} data-testid={`info-${field.key}`}
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Selecciona un producto para ver los gastos de cobranza que se sellarán al contratar.
        </div>
      );
    }
    const val = Number(prod.gastos_cobranza || 0);
    if (!Number.isFinite(val) || val <= 0) {
      return (
        <div id={id} data-testid={`info-${field.key}`}
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
          El producto NO cobra gastos de cobranza.
        </div>
      );
    }
    const tipo = String(prod.gastos_cobranza_tipo || "porcentaje").toLowerCase();
    const monto = tipo === "monto"
      ? `$${Number(val).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (monto fijo)`
      : `${val}% del capital vencido`;
    const disp = Math.max(1, Number(prod.gc_disparo_dias || 1));
    const etq = prod.gc_etiqueta || "Gastos de cobranza";
    return (
      <div id={id} data-testid={`info-${field.key}`}
        className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900 space-y-0.5">
        <p><b>Gastos de cobranza:</b> <span data-testid={`${field.key}-monto`}>{monto}</span></p>
        <p><b>Etiqueta:</b> {etq}</p>
        <p><b>Se aplica automáticamente</b> a partir del día <b>{disp}</b> de atraso.</p>
        <p className="text-[10.5px] text-orange-800">Se sella desde el producto y no forma parte de las cuotas regulares.</p>
      </div>
    );
  }
  // (jul-2026 Fix Parte 5) Resumen en lenguaje llano del producto, actualizado en vivo.
  // Solo presentación; NO viaja en el payload; NO persiste ningún cálculo.
  if (field.type === "resumen_producto_credito") {
    return <ResumenProductoCredito form={form} />;
  }
  // Cambio 3: actividad_economica como select del catálogo curado, con etiqueta [AV] visible y
  // opción "Otro (especificar)" que dispara un input de texto libre en el field siguiente (showIf).
  // (jul-2026) Cuando el usuario elige "Otro", además del texto libre se le ofrece un buscador
  // para elegir la clave oficial ACTIVIDAD_ECONOMICA (RIPS F36). La clave se guarda en
  // `actividad_economica_clave` y la descripción legible en `actividad_economica_descripcion`.
  if (field.type === "actividad_select")
    return (
      <div>
        <ActividadEconomicaCombobox
          value={value ?? ""}
          id={id}
          testid={`select-${field.key}`}
          onValueChange={(v) => {
            onChange(field.key, v);
            // Si el usuario cambia a algo distinto de "Otro (especificar)", limpiar el texto libre
            // Y la clave oficial elegida a mano (el servidor resolverá la clave del nuevo texto).
            if (v !== "Otro (especificar)") {
              if (form.actividad_economica_otro) onChange("actividad_economica_otro", "");
              if (form.actividad_economica_clave) {
                onChange("actividad_economica_clave", "");
                onChange("actividad_economica_descripcion", "");
              }
            }
          }}
        />
        {value === "Otro (especificar)" && (
          <ActividadOtroBuscador
            claveActual={form.actividad_economica_clave || ""}
            descripcionActual={form.actividad_economica_descripcion || ""}
            onSelect={(clave, desc) => {
              onChange("actividad_economica_clave", clave);
              onChange("actividad_economica_descripcion", desc);
            }}
          />
        )}
      </div>
    );
  // Cambio 4: select dinámico del catálogo de productos de la entidad, guardando el NOMBRE del
  // producto en la clave del field (no el id). Se usa en clientes.producto para que el motor de
  // riesgo (que compara cliente.producto contra productos_alto_riesgo) haga match exacto.
  if (field.type === "producto_nombre_select" || (field.type === "producto_select" && field.key === "producto"))
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder="Selecciona un producto..." />
        </SelectTrigger>
        <SelectContent>
          {productos.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-amber-600" data-testid="producto-sin-catalogo">
              No hay productos configurados. Configúralos en “Productos de crédito”.
            </div>
          )}
          {productos.map((p) => {
            // BUILD 1 rangos: muestra el rango si existe, o el valor único legado.
            const _mn = p.tasa_ordinaria_min ?? p.tasa_ordinaria;
            const _mx = p.tasa_ordinaria_max ?? p.tasa_ordinaria;
            const _tasaTxt = _mn == null
              ? ""
              : _mn === _mx || _mx == null
                ? ` · ${_mn}% ord.`
                : ` · ${_mn}%–${_mx}% ord.`;
            return <SelectItem key={p.id} value={p.nombre}>{p.nombre}{_tasaTxt}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    );
  if (field.type === "producto_select")
    return (
      <Select value={value ?? ""} onValueChange={(v) => onProducto(v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder="Selecciona un producto..." />
        </SelectTrigger>
        <SelectContent>
          {productos.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">Define productos en “Productos de crédito”</div>}
          {productos.map((p) => {
            const _mn = p.tasa_ordinaria_min ?? p.tasa_ordinaria;
            const _mx = p.tasa_ordinaria_max ?? p.tasa_ordinaria;
            const _tasaTxt = _mn == null
              ? ""
              : _mn === _mx || _mx == null
                ? ` · ${_mn}% ord.`
                : ` · ${_mn}%–${_mx}% ord.`;
            return <SelectItem key={p.id} value={p.id}>{p.nombre}{_tasaTxt}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    );
  // (duplicado eliminado: bloque específico consolidado arriba)
  if (field.type === "producto_nombre_select_deprecated")
    return null;
  if (field.type === "cliente_origen_select")
    return <OrigenClienteSelect form={form} onCliente={onCliente} />;
  if (field.type === "cliente_select") {
    const locked = field.lockedBy && !!form[field.lockedBy];
    return (
      <ComboSelect
        testid={`select-${field.key}`}
        items={clientes}
        value={form.cliente_id ?? ""}
        disabled={locked}
        fallbackLabel={value || ""}
        onSelect={(c) => onCliente(c)}
        getLabel={(c) => c.nombre}
        getSub={(c) => [c.rfc, c.tipo_persona].filter(Boolean).join(" · ")}
        getSearch={(c) => `${c.nombre || ""} ${c.rfc || ""}`}
        placeholder={locked ? "Derivado del crédito" : "Selecciona un cliente..."}
        searchPlaceholder="Buscar por nombre o RFC..."
        emptyText="No hay clientes. Da de alta clientes primero."
      />
    );
  }
  if (field.type === "pagador_factoraje")
    return (
      <PagadorFactorajeSelect credito={creditos.find((c) => c.id === form.credito_id)} value={value}
        nombreTercero={form.nombre_tercero} showLabel={false}
        onChange={(patch) => Object.entries(patch).forEach(([k, v]) => onChange(k, v))} />
    );
  if (field.type === "credito_select")
    return (
      <ComboSelect
        testid={`select-${field.key}`}
        items={creditos}
        value={value ?? ""}
        fallbackLabel={form.numero_credito || ""}
        onSelect={(c) => onCredito(c)}
        onQueryChange={onCreditoQuery}
        getKey={(c) => c.id}
        getLabel={(c) => c.numero_credito || c.id}
        getSub={(c) => [c.cliente, c.saldo_insoluto != null ? `Saldo $${Number(c.saldo_insoluto).toLocaleString("es-MX")}` : ""].filter(Boolean).join(" · ")}
        getSearch={(c) => `${c.numero_credito || ""} ${c.cliente || ""}`}
        placeholder="Selecciona un crédito..."
        searchPlaceholder="Buscar por no. de crédito o cliente..."
        emptyText="No hay créditos disponibles."
      />
    );
  // ─────────── plazos_multi: multiselect numérico del rango PLAZOS_PERMITIDOS_RANGO (4..60).
  // Renderiza una grilla de checkboxes; value es lista de enteros. Guarda ordenado y único.
  if (field.type === "plazos_multi")
    return <PlazosMultiControl field={field} value={value} id={id} onChange={onChange} />;
  // ─────────── ancla_pago: selector dependiente de la periodicidad del producto elegido
  // (día de semana / convención quincenal / día del mes). Opcional: vacío = sin ancla.
  if (field.type === "ancla_pago") {
    const prodAncla = productos.find((p) => p.id === form.producto_id);
    return (
      <AnclaPagoSelect periodicidad={prodAncla?.periodicidad || form.periodicidad || ""}
        value={value} onChange={(v) => onChange(field.key, v)} testid={`select-${field.key}`} />
    );
  }
  // ─────────── plazo_from_producto: Select dinámico limitado a `producto.plazos_permitidos`.
  // Si el producto no está seleccionado o no tiene lista, cae a input numérico con range 4..60.
  if (field.type === "plazo_from_producto")
    return <PlazoFromProductoControl field={field} value={value} id={id} onChange={onChange}
      producto={productos.find((p) => p.id === form.producto_id) || null} />;
  if (field.type === "readonly_risk" || field.type === "readonly_lpb" || field.type === "readonly") {
    // feb-2026 · iter 07-26 — El tipo `readonly` acepta booleans (usado para
    // `pep_detectado` cuando el hook sella true/false). Los strings pasan directo.
    const rawValue = value;
    const v = typeof rawValue === "boolean"
      ? (rawValue ? "Sí" : "No")
      : (rawValue ?? "");
    const tone =
      field.type === "readonly_lpb"
        ? (v === "Coincidencia" ? "bg-red-100 text-red-700 border-red-200" : v ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200")
        : field.type === "readonly_risk"
          ? (v === "Alto" ? "bg-red-100 text-red-700 border-red-200" : v === "Medio" ? "bg-amber-100 text-amber-700 border-amber-200" : v === "Bajo" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200")
          // readonly genérico con soporte para booleans (sí=rojo=probable riesgo, no=verde).
          : (v === "Sí" ? "bg-red-100 text-red-700 border-red-200"
             : v === "No" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
             : v === "Completo" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
             : v === "Incompleto" ? "bg-amber-100 text-amber-700 border-amber-200"
             : "bg-slate-100 text-slate-500 border-slate-200");
    return (
      <div id={id} data-testid={`readonly-${field.key}`}
        className={`flex h-9 items-center rounded-md border px-3 text-sm font-medium ${tone}`}>
        {v === "" ? "Se determina automáticamente al guardar" : v}
      </div>
    );
  }
  // Buscador de LOCALIDAD oficial CNBV (jul-2026). Guarda descripción legible en
  // `localidad` y clave oficial de 8 dígitos en `localidad_clave`. Además persiste
  // un snapshot del `entidad_federativa` vigente al momento del match, para trazabilidad.
  if (field.type === "localidad_buscador")
    return (
      <div>
        <LocalidadBuscador
          claveActual={form.localidad_clave || ""}
          descripcionActual={form.localidad || ""}
          entidadFederativa={form.entidad_federativa || ""}
          onSelect={(clave, desc) => {
            onChange("localidad_clave", clave);
            onChange("localidad", desc);
            // Snapshot del estado con el que se hizo el match (auditoría).
            onChange("localidad_entidad_snapshot", clave ? (form.entidad_federativa || "") : "");
          }}
        />
      </div>
    );
  if (field.type === "textarea")
    return (
      <Textarea id={id} data-testid={`input-${field.key}`} value={value || ""} rows={3}
        onChange={(e) => onChange(field.key, e.target.value)} />
    );
  // feb-2026: Captura de OBLIGADOS SOLIDARIOS condicionada al producto elegido.
  // Se muestra 1 o 2 tarjetas según `producto.obligados_solidarios` (0=oculto).
  // El valor es un array `[{nombre, domicilio, telefono}, ...]`. NO son sujetos obligados:
  // el schema del backend no los relaciona a KYC/expediente/screening/riesgo.
  // Build 1 · Factoraje: bloque «Datos de la factura»; el monto solicitado es el anticipo.
  if (field.type === "factoraje_captura") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    return <FactorajeCaptura form={form} producto={prod}
      onChange={(k, v) => { onChange(k, v); onChange("monto_solicitado", v?.anticipo ?? ""); }} />;
  }
  // Build 1 · Arrendamiento: bloque «Datos del bien»; el monto solicitado es el capital a arrendar.
  if (field.type === "arrendamiento_captura") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    return <ArrendamientoCaptura form={form} producto={prod}
      onChange={(k, v) => { onChange(k, v); onChange("monto_solicitado", v?.capital_arrendar ?? ""); }} />;
  }
  if (field.type === "obligados_solidarios_captura") {
    const prod = productos.find((p) => p.id === form.producto_id) || null;
    const num = Math.max(0, Math.min(2, parseInt(prod?.obligados_solidarios || 0, 10) || 0));
    if (num === 0) {
      return (
        <div id={id} data-testid="obligados-oculto" className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs italic text-slate-400">
          Este producto no requiere obligados solidarios.
        </div>
      );
    }
    const lista = Array.isArray(value) ? value : [];
    const setOblig = (idx, patch) => {
      const next = Array.from({ length: num }, (_, i) => ({
        nombre: (lista[i] || {}).nombre || "",
        domicilio: (lista[i] || {}).domicilio || "",
        telefono: (lista[i] || {}).telefono || "",
      }));
      next[idx] = { ...next[idx], ...patch };
      onChange(field.key, next);
    };
    return (
      <div id={id} data-testid="obligados-solidarios-captura" className="space-y-3">
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          Los obligados solidarios NO entran al expediente ni al screening. Se capturan como referencia de cobranza (nombre, domicilio y teléfono). Los documentos y contrato los administra tu SOFOM.
        </p>
        {Array.from({ length: num }).map((_, i) => {
          const o = lista[i] || {};
          return (
            <div key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`obligado-${i + 1}-card`}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Obligado solidario {i + 1}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-xs">Nombre completo</Label>
                  <Input data-testid={`obligado-${i + 1}-nombre`} value={o.nombre || ""}
                    onChange={(e) => setOblig(i, { nombre: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-xs">Domicilio</Label>
                  <Textarea data-testid={`obligado-${i + 1}-domicilio`} value={o.domicilio || ""} rows={2}
                    onChange={(e) => setOblig(i, { domicilio: e.target.value })} />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Teléfono</Label>
                  <Input data-testid={`obligado-${i + 1}-telefono`} value={o.telefono || ""}
                    onChange={(e) => setOblig(i, { telefono: e.target.value })} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  // Referencias personales (oct-2026): hasta 2 personas de contacto, fijas (no dependen del
  // producto). Son solo contacto — NO entran al expediente ni al screening.
  if (field.type === "referencias_captura") {
    const lista = Array.isArray(value) ? value : [];
    const setRef = (idx, patch) => {
      const next = Array.from({ length: 2 }, (_, i) => ({
        nombre: (lista[i] || {}).nombre || "",
        telefono: (lista[i] || {}).telefono || "",
        domicilio: (lista[i] || {}).domicilio || "",
        ocupacion: (lista[i] || {}).ocupacion || "",
      }));
      next[idx] = { ...next[idx], ...patch };
      onChange(field.key, next);
    };
    return (
      <div id={id} data-testid="referencias-captura" className="space-y-3">
        <p className="text-[11px] text-slate-400">
          Solo contacto. No entran al expediente ni al screening.
        </p>
        {Array.from({ length: 2 }).map((_, i) => {
          const r = lista[i] || {};
          return (
            <div key={i} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`referencia-${i + 1}-card`}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Referencia {i + 1}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-xs">Nombre completo</Label>
                  <Input data-testid={`referencia-${i + 1}-nombre`} value={r.nombre || ""}
                    onChange={(e) => setRef(i, { nombre: e.target.value })} />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Teléfono</Label>
                  <Input data-testid={`referencia-${i + 1}-telefono`} value={r.telefono || ""}
                    onChange={(e) => setRef(i, { telefono: e.target.value })} />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">A qué se dedica</Label>
                  <Input data-testid={`referencia-${i + 1}-ocupacion`} value={r.ocupacion || ""}
                    onChange={(e) => setRef(i, { ocupacion: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-1 block text-xs">Domicilio</Label>
                  <Textarea data-testid={`referencia-${i + 1}-domicilio`} value={r.domicilio || ""} rows={2}
                    onChange={(e) => setRef(i, { domicilio: e.target.value })} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  // Nombre con desglose PF (feb-2026): si el formulario ya tiene `nombres` O `apellido_paterno`
  // capturado (PF con desglose), este campo queda de SOLO LECTURA y muestra el nombre compuesto
  // en vivo (el backend lo re-compone al guardar como fuente única de verdad).
  // Si NO hay desglose (legacy o PM), sigue editable + hint "sin desglosar" para PF legacy.
  if (field.type === "nombre_con_desglose") {
    const _tp = String(form.tipo_persona || "");
    const _isPF = TP_PF_SET.has(_tp);
    const _nombres = String(form.nombres || "").trim();
    const _apPat = String(form.apellido_paterno || "").trim();
    const _apMat = String(form.apellido_materno || "").trim();
    const _tieneDesglose = _isPF && (_nombres || _apPat);
    if (_tieneDesglose) {
      // Composición en vivo — el backend re-compone al guardar (fuente única de verdad).
      const _compuesto = [_nombres, _apPat, _apMat].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
      return (
        <div className="flex flex-col gap-1">
          <div id={id} data-testid={`readonly-${field.key}`}
            className="flex h-9 items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-900">
            {_compuesto || (value || "")}
          </div>
          <p className="text-[11px] leading-tight text-emerald-800">
            Compuesto automáticamente desde el desglose. Para editarlo, corrige nombre(s) o apellidos arriba.
          </p>
        </div>
      );
    }
    // Sin desglose: editable como antes. Si es PF, muestra hint "sin desglosar" para
    // invitar a completar el desglose (requerido por el layout PLD Anexo XVIII CNBV).
    return (
      <div className="flex flex-col gap-1">
        <Input id={id} data-testid={`input-${field.key}`} type="text"
          value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value)} />
        {_isPF && (
          <p data-testid={`hint-nombre-sin-desglose`}
            className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-tight text-amber-900">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
            <span>
              <b>Nombre sin desglosar.</b> Captura arriba nombre(s), apellido paterno y (opcional)
              apellido materno para cumplir con el layout Anexo XVIII CNBV. Este campo se
              compondrá automáticamente cuando lo hagas.
            </span>
          </p>
        )}
      </div>
    );
  }
  if (field.type === "bool") {
    // Sincronización dura Medio de pago ↔ toggle PLD (feb-2026):
    // en el recurso "pagos", el toggle "¿Pago en efectivo?" queda EN CONFLICTO cuando el
    // operador seleccionó "Efectivo" en el select y luego APAGÓ manualmente el toggle.
    // No lo bloqueamos (el operador puede tener un caso raro tipo depósito ventanilla),
    // pero mostramos advertencia clara: el backend igual forzará pago_efectivo=True para
    // que el motor PLD contabilice el efectivo (defensa en profundidad).
    const conflictoPldEfectivo =
      field.key === "pago_efectivo" &&
      String(form.medio_pago || "").toLowerCase() === "efectivo" &&
      !value;
    return (
      <div className="flex flex-col gap-1">
        <div className="flex h-9 items-center">
          <Switch id={id} data-testid={`switch-${field.key}`} checked={!!value}
            onCheckedChange={(v) => onChange(field.key, v)} />
        </div>
        {conflictoPldEfectivo && (
          <p data-testid={`warn-${field.key}`}
             className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-tight text-amber-900">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
            <span>
              Seleccionaste <b>Efectivo</b> como medio de pago. Se registrará en el
              monitoreo PLD como efectivo aunque este toggle esté apagado.
            </span>
          </p>
        )}
      </div>
    );
  }
  if (field.type === "select")
    return (
      // sep-2026 · el valor se compara como TEXTO. Varios campos se guardan numéricos
      // (`iva: 16`, catálogos cerrados) y Radix no reconocía el número contra el
      // `value="16"` de la opción: el selector abría VACÍO al reabrir el registro.
      <Select value={value === undefined || value === null || value === "" ? "" : String(value)}
        onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder="Seleccionar..." />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lbl = typeof o === "string" ? o : o.label;
            return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
          })}
        </SelectContent>
      </Select>
    );
  if (field.type === "regimen_fiscal_select") {
    // Selector de Régimen fiscal SAT filtrado por tipo_persona del registro actual (form).
    // Muestra "código — descripción". Regímenes 610/616/626 aplican a ambos.
    // Fuente única: /api/catalogos-maestros.regimenes_fiscales_sat (precargado en window).
    const tp = String(form.tipo_persona || "").toLowerCase();
    const esMoral = /(moral|sociedad|empresa|fideicomiso)/.test(tp);
    const REG_ALL = (typeof window !== "undefined" && window.__REGIMENES_FISCALES_SAT__) || [];
    const opciones = REG_ALL.filter(r =>
      r.aplicabilidad === "ambos" || (esMoral ? r.aplicabilidad === "moral" : r.aplicabilidad === "fisica")
    );
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger id={id} data-testid={`select-${field.key}`}>
          <SelectValue placeholder={tp ? "Elige régimen SAT..." : "Primero elige tipo de persona"} />
        </SelectTrigger>
        <SelectContent>
          {opciones.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-amber-600">
              Elige primero el tipo de persona
            </div>
          )}
          {opciones.map(r => (
            <SelectItem key={r.codigo} value={r.codigo}
                        data-testid={`select-${field.key}-opt-${r.codigo}`}>
              {r.codigo} — {r.descripcion}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "year") {
    // La credencial INE solo trae el AÑO de vigencia. Se hidrata retro-compatible:
    // valores viejos tipo "2035-01-01" se muestran como "2035".
    const raw = value ?? "";
    const shown = typeof raw === "string" && /^\d{4}/.test(raw) ? raw.slice(0, 4) : "";
    return (
      <Input id={id} data-testid={`input-${field.key}`} type="number"
        inputMode="numeric" min={2020} max={2040} step={1} maxLength={4}
        placeholder="Año de vigencia (aaaa)"
        value={shown}
        onChange={(e) => {
          const only = String(e.target.value || "").replace(/\D+/g, "").slice(0, 4);
          onChange(field.key, only);
        }} />
    );
  }
  // money: input de montos en pesos con separador de miles EN VIVO. Guarda el valor crudo.
  if (field.type === "money")
    return (
      <MoneyInput id={id} data-testid={`input-${field.key}`}
        value={value ?? ""} onChange={(v) => onChange(field.key, v)} />
    );
  return (
    <Input id={id} data-testid={`input-${field.key}`}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
      value={value ?? ""}
      maxLength={field.maxLength || undefined}
      pattern={field.pattern || undefined}
      inputMode={field.inputMode || undefined}
      onChange={(e) => {
        let v = e.target.value;
        // Si el field declara maxLength, aplicamos hard cap (defensa en profundidad; el
        // attr `maxLength` solo protege teclado, no paste).
        if (field.maxLength) v = String(v || "").slice(0, field.maxLength);
        // Si declara `pattern` de solo dígitos (`\d{N}`), filtramos no-dígitos aquí para UX
        // predecible en móvil/paste.
        if (field.pattern && /^\\d\{\d+\}$/.test(field.pattern)) {
          const m = field.pattern.match(/^\\d\{(\d+)\}$/);
          const len = m ? parseInt(m[1], 10) : null;
          v = String(v || "").replace(/\D+/g, "");
          if (len) v = v.slice(0, len);
        }
        onChange(field.key, v);
      }} />
  );
}

// sep-2026 · Texto de un campo SELLADO (solo lectura). Se pinta en lugar del control para
// que la condición económica no se pueda tocar después de enviar la solicitud al Comité.
function textoSellado(field, form) {
  const k = field.key;
  const v = form[k];
  if (k === "producto_id") return form.producto || form.producto_id || "—";
  if (k === "plazo") return formatearPlazo(form.plazo, form.periodicidad);
  if (k === "periodicidad") return etiquetaPeriodicidad(form.periodicidad) || form.periodicidad || "—";
  if (k === "comision_apertura_tipo_readonly")
    return form.comision_apertura_tipo === "monto" ? "Monto fijo" : "Porcentaje";
  if (k === "comision_apertura")
    return form.comision_apertura_tipo === "monto" ? mxn(form.comision_apertura) : `${v ?? "—"}%`;
  if (["tasa_ordinaria", "tasa_moratoria", "iva"].includes(k))
    return v === undefined || v === null || v === "" ? "—" : `${v}%`;
  if (["monto_solicitado", "monto_autorizado"].includes(k)) return mxn(v);
  return v === undefined || v === null || v === "" ? "—" : String(v);
}

export function ResourceManager({ config, extraToolbar, extraRowActions, extraEditContent }) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const canCreate = can(config.name, "crear");
  const canEdit = can(config.name, "editar");
  const canDelete = can(config.name, "eliminar") && !config.hideDelete;
  const isEntidad = config.name === "entidades";
  const canExport = can(config.name, "descargar");
  const canView = !!config.viewRoute && user?.role === "super_admin";
  // Build 3 · Factoraje: la casilla «Deudor de factoraje» solo se ve si la entidad opera factoraje.
  const entidadConFactoraje = useFactorajeHabilitado(
    !!config.fields?.some((f) => f.soloEntidadFactoraje) && user?.role !== "super_admin");
  // Build 1 · Arrendamiento: la casilla «Proveedor de arrendamiento» solo si la entidad lo opera.
  const entidadConArrendamiento = useArrendamientoHabilitado(
    !!config.fields?.some((f) => f.soloEntidadArrendamiento) && user?.role !== "super_admin");

  const goView = (row) => navigate(`${config.viewRoute}/${row.id}`);
  // ago-2026 · Fase 2 — Contador de ALTAS del mes por entidad (solo super_admin, informativo).
  const [altasUso, setAltasUso] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [screeningPid, setScreeningPid] = useState(null);
  const [geoPoint, setGeoPoint] = useState(null);
  const [eliminarEntidad, setEliminarEntidad] = useState(null);
  // Fase 8+ · Feb-2026 — Diálogo de "Cancelar cliente" (baja lógica) con conteos reales
  // de operación (créditos, pagos, garantías, líneas y expedientes). Reemplaza al
  // `window.confirm` genérico SOLO para el recurso `clientes`. El resto de recursos
  // conserva el flujo previo (créditos y garantías tienen sus propias reglas de baja).
  // El backend sigue siendo el mismo `DELETE /api/clientes/{id}` (baja lógica sellada).
  const [cancelarCliente, setCancelarCliente] = useState(null);        // {id, nombre, rfc, estatus}
  const [cancelarConteos, setCancelarConteos] = useState(null);        // {creditos, cred_vigentes, ...} | null (cargando)
  const [cancelarConfirmTexto, setCancelarConfirmTexto] = useState("");
  const [cancelarEjecutando, setCancelarEjecutando] = useState(false);
  // Recurso "pagos": desglose del monto sugerido al elegir un crédito (cuota + diferidos).
  // Alinea la pantalla genérica con Cartera/Cobranza (opción 1 del audit, jul-2026).
  const [pagoSugerido, setPagoSugerido] = useState(null);
  const [pagoExigiblePorIntegrante, setPagoExigiblePorIntegrante] = useState([]);
  // Tabla seleccionable en el modal de Pagos (jul-2026): crédito elegido + cuota resaltada.
  // La selección es AYUDA VISUAL (pre-llena el monto); NO cambia la prelación del motor.
  const [pagoCredito, setPagoCredito] = useState(null);
  const TablaPago = esCreditoArrendamiento(pagoCredito) ? TablaRentasCredito : TablaAmortizacionCredito;
  const [pagoCuotaSel, setPagoCuotaSel] = useState(null);
  // Catálogo cerrado de países (ISO 3166-1 + gentilicios). Se carga bajo demanda cuando el
  // formulario tiene fields nacionalidad_select o pais_select. La lista GAFI de riesgo NO
  // viaja aquí (queda opaca al operador) — el cruce por ISO ocurre en el backend/EBR.
  const [paises, setPaises] = useState([]);
  // Candado de PRODUCTOS DE CRÉDITO por plan (jul-2026, iter_80). Solo aplica al recurso
  // "productos_credito"; carga el uso desde /facturacion/uso-productos y alimenta el chip
  // "N/M activos" + gating del botón Nuevo. Grandfathered si enforcement=false.
  const [usoProductos, setUsoProductos] = useState(null);
  // sep-2026 · SELLADO de la solicitud: al enviarla al Comité sus condiciones económicas
  // dejan de ser editables (el backend responde 403 al PUT). Aquí se pintan bloqueadas.
  const camposSellados = useMemo(
    () => (config.name === "solicitudes" && editing ? camposSelladosDe(editing) : []),
    [config.name, editing]);

  const hasProductoField = config.fields?.some((f) => f.type === "producto_select" || f.type === "producto_nombre_select");
  const hasClienteField = config.fields?.some((f) => f.type === "cliente_select");
  const hasCreditoField = config.fields?.some((f) => f.type === "credito_select");
  const hasPaisField = config.fields?.some((f) => f.type === "nacionalidad_select" || f.type === "pais_select");
  const esProductosCredito = config.name === "productos_credito";

  useEffect(() => {
    if (hasProductoField) listResource("productos_credito").then(setProductos).catch(() => {});
  }, [hasProductoField]);

  useEffect(() => {
    if (hasClienteField) listResource("clientes").then(setClientes).catch(() => {});
  }, [hasClienteField]);

  // ago-2026 · Fase 2 — Consumo de altas del mes por entidad (columna informativa).
  const cargarAltasUso = useCallback(() => {
    if (!(isEntidad && user?.role === "super_admin")) return;
    api.get("/admin/entidades-altas-uso")
      .then((r) => setAltasUso(r.data))
      .catch(() => setAltasUso(null));
  }, [isEntidad, user?.role]);

  useEffect(() => { cargarAltasUso(); }, [cargarAltasUso]);

  useEffect(() => {
    if (hasCreditoField) listResource("creditos").then(setCreditos).catch(() => {});
  }, [hasCreditoField]);

  // FIX cuota-stale (jul-2026): re-cargar `creditos` y `clientes` cada vez que se abre el modal
  // de alta. Sin esto, el objeto del crédito local queda desactualizado tras un pago:
  // `onCredito` leería `tabla_amortizacion` pre-pago (cuota #1 como "Pendiente" aunque ya
  // esté saldada en la BD), y `.find(cu.estatus !== "Pagada")` devolvería la #1 en vez de la #2.
  // Consecuencia doble evitada: (a) el sugerido avanza a la primera cuota realmente pendiente
  // y (b) el moratorio del fix 5 aparece cuando la nueva cuota candidato ya está vencida.
  useEffect(() => {
    if (!open) return;
    if (hasCreditoField) listResource("creditos").then(setCreditos).catch(() => {});
    if (hasClienteField) listResource("clientes").then(setClientes).catch(() => {});
  }, [open, hasCreditoField, hasClienteField]);

  useEffect(() => {
    if (!hasPaisField) return;
    api.get("/catalogos-maestros").then((res) => {
      setPaises(res?.data?.paises || res?.paises || []);
    }).catch(() => {});
  }, [hasPaisField]);

  // Régimen fiscal SAT — precargar catálogo global 1 vez (window.__REGIMENES_FISCALES_SAT__).
  // El select `regimen_fiscal_select` lo lee al renderizar. Filtrado por tipo_persona al render.
  useEffect(() => {
    if (typeof window !== "undefined" && window.__REGIMENES_FISCALES_SAT__) return;
    api.get("/catalogos-maestros").then((res) => {
      const list = res?.data?.regimenes_fiscales_sat || res?.regimenes_fiscales_sat || [];
      if (list.length && typeof window !== "undefined") {
        window.__REGIMENES_FISCALES_SAT__ = list;
      }
    }).catch(() => {});
  }, []);

  const cargarUsoProductos = () => {
    if (!esProductosCredito) return;
    api.get("/facturacion/uso-productos").then((res) => {
      setUsoProductos(res?.data || null);
    }).catch(() => setUsoProductos(null));
  };
  useEffect(() => {
    cargarUsoProductos();
  }, [esProductosCredito]);  // cargarUsoProductos es estable; solo depende de esProductosCredito

  const onProducto = (prodId) => {
    const p = productos.find((x) => x.id === prodId);
    if (!p) return;
    // BUILD 1 rangos (feb-2026): si el producto declara rango de tasa, precarga con el
    // MÍNIMO del rango (política PO: tasa más barata por default; ejecutivo puede subirla
    // dentro del rango). Retrocompat: si el producto solo tiene el campo único legado,
    // se precarga ese valor.
    const tasaOrdPrecarga = p.tasa_ordinaria_min ?? p.tasa_ordinaria;
    const tasaMorPrecarga = p.tasa_moratoria_min ?? p.tasa_moratoria;
    setForm((prev) => ({
      ...prev,
      producto_id: p.id, producto: p.nombre,
      tasa_ordinaria: tasaOrdPrecarga ?? prev.tasa_ordinaria,
      tasa_moratoria: tasaMorPrecarga ?? prev.tasa_moratoria,
      comision_apertura: p.comision_apertura ?? prev.comision_apertura,
      iva: p.iva ?? prev.iva,
      periodicidad: p.periodicidad ?? prev.periodicidad,
      tipo_amortizacion: p.tipo_amortizacion ?? prev.tipo_amortizacion,
      gastos_cobranza: p.gastos_cobranza ?? prev.gastos_cobranza,
    }));
  };

  // Guarda la referencia real del cliente (cliente_id) + el nombre para mostrar.
  const onCliente = (c) => {
    if (!c) return;
    setForm((prev) => ({ ...prev, cliente_id: c.id, cliente: c.nombre }));
  };

  // En Pagos, el crédito deriva el cliente y autollena/bloquea los campos.
  // En Garantías, además hereda el tipo de garantía derivado del producto.
  const onCredito = (c) => {
    if (!c) return;
    const hasTipoGarantia = config.fields?.some((f) => f.key === "tipo_garantia");
    setForm((prev) => ({
      ...prev,
      credito_id: c.id,
      numero_credito: c.numero_credito ?? prev.numero_credito,
      cliente_id: c.cliente_id ?? prev.cliente_id,
      cliente: c.cliente ?? prev.cliente,
      ...(hasTipoGarantia && c.garantia_tipo ? { tipo_garantia: c.garantia_tipo } : {}),
    }));
    // Recurso "pagos" — pre-llenar el monto con el mismo sugerido que Cartera/Cobranza:
    // cuota del próximo periodo + moratorio diferido pendiente + GC diferidos pendientes.
    // Solo aplica en alta nueva (no al editar un pago histórico).
    if (config.name === "pagos" && !editing) {
      const prox = (c.tabla_amortizacion || []).find((cu) => cu.estatus !== "Pagada");
      // Tabla seleccionable (jul-2026): por default se preselecciona la primera cuota
      // pendiente (comportamiento de siempre); el usuario puede elegir otra en la tabla.
      setPagoCredito(c);
      setPagoCuotaSel(prox?.no ?? null);
      aplicarSugerido(c, prox);
      // iter124 (caso ZINALI CR-2026-006) — Si el crédito es GRUPAL, pedimos el
      // desglose por integrante al backend. Se muestra en el modal para que el
      // operador vea cuánto le toca a cada quien (regla dura del PO).
      if (c.id && Array.isArray(c.integrantes) && c.integrantes.length > 0) {
        api.get(`/creditos/${c.id}/exigible`).then(({ data }) => {
          setPagoExigiblePorIntegrante(data?.exigible_por_integrante || []);
        }).catch(() => setPagoExigiblePorIntegrante([]));
      } else {
        setPagoExigiblePorIntegrante([]);
      }
      // Mora AL DÍA (jul-2026): mismo endpoint del motor que usa Cartera al abrir el
      // detalle. Refresca estatus/mora devengada de cada cuota sin recalcular en frontend.
      if (c.id && (c.tabla_amortizacion || []).length) {
        api.post(`/creditos/${c.id}/recalcular-mora`).then(({ data }) => {
          if (!data?.tabla_amortizacion?.length) return;
          setPagoCredito(data);
          const proxFresco = data.tabla_amortizacion.find((cu) => cu.estatus !== "Pagada");
          setPagoCuotaSel(proxFresco?.no ?? null);
          aplicarSugerido(data, proxFresco);
        }).catch(() => { /* rol sin permiso o crédito legacy: se queda la tabla almacenada */ });
      }
    }
  };

  // Sugerido UNIFICADO (jul-2026): util compartida con Cartera/Cobranza. Los importes se
  // LEEN de la tabla SELLADA y de la mora persistida por el motor (base 360). El frontend
  // NO recalcula mora — el fallback capital × tasa/365 fue eliminado (audit jul-2026).
  const aplicarSugerido = (c, prox) => {
      const sug = sugeridoDePago(c, prox);
      // feb-2026 iter123 (caso ZINALI CR-2026-005) — desglose ORDENADO según prelación
      // exacta del motor. El "sugerido" clásico se conserva para compatibilidad de
      // otros consumos; el modal usa `sug.desglosePrelacion` como fuente de verdad
      // ordenada 1..13 (mora dif → GC → cuota → capital → mora corriente).
      const dp = desgloseOrdenado(c, prox);
      sug.desglosePrelacion = dp.items;
      sug.totalPrelacion = dp.total;
      setPagoSugerido(sug);
      if (sug.total > 0) {
        setForm((prev) => ({ ...prev, monto: String(sug.total) }));
      }
  };

  // Clic en una fila de la tabla del modal de Pagos: resalta la cuota y pre-llena su
  // PENDIENTE sellado. Ayuda visual solamente — el motor aplica con su prelación.
  const onSelectCuota = (cu) => {
    if (!pagoCredito || !cu) return;
    setPagoCuotaSel(cu.no);
    aplicarSugerido(pagoCredito, cu);
  };

  // Búsqueda de créditos también en BACKEND (jul-2026): al teclear en el combo se consulta
  // GET /creditos?search= y los resultados se AGREGAN (dedupe por id) a la lista local. Así
  // el selector encuentra cualquier crédito de la entidad aunque haya cientos, en vez de
  // filtrar solo lo pre-cargado en memoria.
  const buscarCreditosBackend = useMemo(() => {
    let timer = null;
    return (q) => {
      if (!hasCreditoField) return;
      if (timer) clearTimeout(timer);
      const term = String(q || "").trim();
      if (term.length < 2) return;
      timer = setTimeout(async () => {
        try {
          const { data } = await api.get("/creditos", { params: { search: term } });
          if (!Array.isArray(data) || !data.length) return;
          setCreditos((prev) => {
            const ids = new Set(prev.map((x) => x.id));
            const nuevos = data.filter((x) => x.id && !ids.has(x.id));
            return nuevos.length ? [...prev, ...nuevos] : prev;
          });
        } catch { /* búsqueda remota best-effort: el filtro local sigue funcionando */ }
      }, 300);
    };
  }, [hasCreditoField]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listResource(config.name);
      setRows(data);
      // Al listar el recurso productos_credito, refrescar el candado (ej. tras borrar un
      // producto se libera cupo y el chip debe actualizarse sin recargar la página).
      if (esProductosCredito) cargarUsoProductos();
    } catch (e) {
      toast.error("No se pudieron cargar los registros");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [config.name]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
  }, [rows, search]);

  const openNew = () => {
    const init = {};
    config.fields.forEach((f) => { init[f.key] = f.default !== undefined ? f.default : (f.type === "bool" ? false : ""); });
    setForm(init);
    setEditing(null);
    setGeoPoint(null);
    setPagoSugerido(null); setPagoExigiblePorIntegrante([]);
    setPagoCredito(null);
    setPagoCuotaSel(null);
    setOpen(true);
  };

  const openEdit = (row) => {
    // Fase 8+ (feb-2026) — Auto-fill de defaults al editar producto legacy.
    // Los productos creados antes de que ciertos campos fueran obligatorios (ej. `iva`,
    // `base_dias_mora`, `comision_apertura_tipo`) llegan con esos campos en null o
    // undefined. Al abrir el form, aplicamos el `default` del schema para que el select
    // no aparezca vacío. NO se persiste automáticamente — el operador debe guardar para
    // sellar los nuevos valores. Cero mutación de datos existentes en BD.
    const filled = { ...row };
    config.fields.forEach((f) => {
      if (f.default !== undefined) {
        const v = filled[f.key];
        if (v === null || v === undefined || v === "") filled[f.key] = f.default;
      }
    });
    setForm(filled);
    setEditing(row);
    setGeoPoint(null);
    setPagoSugerido(null); setPagoExigiblePorIntegrante([]);
    setPagoCredito(null);
    setPagoCuotaSel(null);
    setOpen(true);
  };

  const onChange = (k, v) => setForm((p) => {
    const next = { ...p, [k]: v };
    // Sincronización dura Medio de pago="efectivo" ↔ toggle "¿Pago en efectivo?" (feb-2026):
    // solo aplica al recurso "pagos". Cuando el operador elige "Efectivo" en el select,
    // encendemos el toggle PLD automáticamente. Si luego apaga el toggle manualmente,
    // FieldControl muestra advertencia y el backend igual forzará True (defensa doble).
    if (config.name === "pagos" && k === "medio_pago" && String(v || "").toLowerCase() === "efectivo") {
      next.pago_efectivo = true;
    }
    if (config.name === "productos_credito" && k === "tipo" && v === TIPO_FACTORAJE) next.modalidad = "Individual";
    // Sugerencia de CP domiciliario ← CP fiscal (jul-2026, clientes/prospectos):
    // cuando el operador captura el CP fiscal en un registro nuevo/edición y el CP del
    // domicilio de contacto está vacío, lo pre-rellenamos con el mismo valor. El operador
    // puede sobreescribirlo si son distintos. NO se toca si ya había captura previa.
    if (k === "codigo_postal_fiscal" && !String(p.codigo_postal ?? "").trim()) {
      const cp = String(v || "").replace(/\D+/g, "").slice(0, 5);
      if (cp.length === 5) next.codigo_postal = cp;
    }
    // Si cambia la entidad_federativa y ya había una localidad elegida cuyo snapshot NO
    // coincide con la nueva entidad, limpiamos localidad/clave: la clave anterior podría
    // pertenecer a otro estado y arrastrarla al reporte SITI sería incorrecto.
    if (k === "entidad_federativa" && p.localidad_clave) {
      const snap = String(p.localidad_entidad_snapshot || "").trim();
      const nuevoEstado = String(v || "").trim();
      if (snap && snap !== nuevoEstado) {
        next.localidad = "";
        next.localidad_clave = "";
        next.localidad_entidad_snapshot = "";
      }
    }
    return next;
  });

  // Bloqueo específico para alta nueva de clientes/prospectos con tipo_persona=fideicomiso.
  // Los fideicomisos se gestionan en el módulo dedicado; en estos flujos solo se aceptan
  // los otros 4 tipos. Al editar registros legacy con tipo fideicomiso, NO se bloquea.
  const fideicomisoBloqueado = !!(
    config.blockNewFideicomiso && !editing && form.tipo_persona === "fideicomiso"
  );

  // Visibilidad de un field en el formulario (usada por render y por save):
  // visibleForRoles + showIf con matchers: equals (default) | oneOf | notEmpty | empty | all.
  // `all`: lista de sub-showIf que deben cumplirse todos (AND lógico). Cada sub-showIf usa
  // el mismo esquema recursivo. Aditivo: si `all` no viene, el comportamiento previo se
  // preserva byte-idéntico para todos los recursos y avisos existentes.
  //
  // feb-2026 · iter 07-26 — Se agregan dos matchers ADITIVOS por giro del usuario para poder
  // condicionar campos SOFOM vs Inmobiliaria en el MISMO schema compartido sin duplicarlo:
  //   { giro: "inmobiliaria" }    → visible solo si user.giro === "inmobiliaria"
  //   { giroNot: "inmobiliaria" } → visible cuando user.giro es distinto (SOFOM, fideicomisos, etc.)
  // Ambos son opcionales; los schemas existentes sin estos matchers siguen comportándose
  // idéntico. El giro proviene del backend en /rbac/me y llega vía useAuth() al top del
  // componente (ya en scope).
  // Build 1 · Factoraje: el tipo del producto (o del producto elegido en la solicitud)
  // decide qué campos se ven. Los demás productos no cambian.
  const formEsFactoraje = config.name === "productos_credito" ? esFactoraje(form)
    : config.name === "solicitudes" ? esFactoraje(productos.find((p) => p.id === form.producto_id))
    : config.name === "pagos" ? esCreditoFactoraje(creditos.find((c) => c.id === form.credito_id)) : false;
  const formEsArrendamiento = config.name === "productos_credito" ? esArrendamiento(form)
    : config.name === "solicitudes" ? esArrendamiento(productos.find((p) => p.id === form.producto_id)) : false;
  const fieldVisible = (f) => {
    if (f.visibleForRoles && !f.visibleForRoles.includes(user?.role)) return false;
    if (f.soloEntidadFactoraje && !entidadConFactoraje) return false;
    if (f.ocultoEnFactoraje && formEsFactoraje) return false;
    if (f.soloFactoraje && !formEsFactoraje) return false;
    if (f.soloEntidadArrendamiento && !entidadConArrendamiento) return false;
    if (f.ocultoEnArrendamiento && formEsArrendamiento) return false;
    if (f.soloArrendamiento && !formEsArrendamiento) return false;
    if (!f.showIf) return true;
    const userGiro = (user?.giro || "").toLowerCase();
    const matchOne = (rule) => {
      if (Array.isArray(rule.all)) return rule.all.every(matchOne);
      if (rule.giro) return userGiro === String(rule.giro).toLowerCase();
      if (rule.giroNot) return userGiro !== String(rule.giroNot).toLowerCase();
      const cur = form[rule.key];
      if (Array.isArray(rule.oneOf)) return rule.oneOf.includes(cur);
      if (rule.notEmpty) return cur != null && String(cur).trim() !== "";
      if (rule.empty) return cur == null || String(cur).trim() === "";
      return cur === rule.equals;
    };
    return matchOne(f.showIf);
  };

  const save = async () => {
    // Bloqueo del alta nueva con tipo_persona=fideicomiso en flujos que no lo soportan.
    if (fideicomisoBloqueado) {
      toast.error("Los fideicomisos se dan de alta en el módulo de Fideicomisos, no desde este formulario.");
      return;
    }
    // Validación de campos obligatorios (front). El backend valida de nuevo (gate duro).
    // Solo cuentan los fields VISIBLES (los condicionados por tipo de persona no aplican
    // cuando están ocultos).
    const faltan = [];
    (config.fields || []).forEach((f) => {
      if (f.required && fieldVisible(f)) {
        const v = form[f.key];
        if (v === "" || v == null) faltan.push(f.label);
      }
    });
    (config.requiredOneOf || []).forEach((r) => {
      if (r.ocultoEnFactoraje && formEsFactoraje) return;
      if (!r.keys.some((k) => form[k] !== "" && form[k] != null)) faltan.push(r.label);
    });
    if (faltan.length) {
      const _msgFaltan = [...new Set(faltan)].join(", ");
      if (config.allowPartialSave) {
        // Guardado PARCIAL permitido (jul-2026, prospectos): los requeridos solo avisan;
        // el candado duro vive en backend (calificar / convertir a cliente).
        toast.warning("Guardado parcial — faltan campos del expediente de datos: " + _msgFaltan);
      } else {
        toast.error("Faltan campos obligatorios: " + _msgFaltan);
        return;
      }
    }
    // oct-2026 — Campos obligatorios SOLO AL CREAR (ej. Origen de los recursos): bloquean
    // el ALTA nueva sin excepción (aunque el recurso tenga `allowPartialSave`); en la EDICIÓN
    // de un registro ya existente que no lo tenga, solo se avisa y el guardado NO se bloquea.
    const faltanSoloCrear = [];
    (config.fields || []).forEach((f) => {
      if (f.requiredCreateOnly && fieldVisible(f)) {
        const v = form[f.key];
        if (v === "" || v == null) faltanSoloCrear.push(f.label);
      }
    });
    if (faltanSoloCrear.length) {
      const _msgCrear = [...new Set(faltanSoloCrear)].join(", ");
      if (editing == null) {
        toast.error("Faltan campos obligatorios: " + _msgCrear);
        return;
      }
      toast.warning("Falta en el expediente: " + _msgCrear);
    }
    // BUILD 1 rangos (feb-2026) — validación local de rango vs producto.
    // Backend valida de nuevo (defensa en profundidad).
    const _prodSel = productos.find((p) => p.id === form.producto_id) || null;
    if (_prodSel) {
      const _rangoErrors = [];
      const _checkRango = (val, mn, mx, label, unidad = "%") => {
        if (val === "" || val == null || mn == null || mx == null || mn === "" || mx === "") return;
        const n = Number(val);
        if (!Number.isFinite(n)) return;
        if (n < Number(mn) || n > Number(mx)) {
          const fmt = unidad === "$"
            ? (x) => Number(x).toLocaleString("es-MX", { style: "currency", currency: "MXN" })
            : (x) => `${x}%`;
          _rangoErrors.push(`${label} (${fmt(n)}) fuera del rango del producto: ${fmt(mn)} – ${fmt(mx)}.`);
        }
      };
      _checkRango(form.tasa_ordinaria, _prodSel.tasa_ordinaria_min, _prodSel.tasa_ordinaria_max, "Tasa ordinaria");
      _checkRango(form.tasa_moratoria, _prodSel.tasa_moratoria_min, _prodSel.tasa_moratoria_max, "Tasa moratoria");
      _checkRango(form.monto_solicitado, _prodSel.monto_min, _prodSel.monto_max, "Monto solicitado", "$");
      if (_rangoErrors.length) {
        toast.error(_rangoErrors.join("  "));
        return;
      }
    }
    setSaving(true);
    const payload = { ...form };
    config.fields.forEach((f) => {
      if ((f.type === "number" || f.type === "money") && payload[f.key] !== "" && payload[f.key] != null)
        payload[f.key] = Number(payload[f.key]);
      // Los campos de solo lectura son RESULTADO del motor (screening/EBR): nunca se envían como captura.
      if (["readonly_risk", "readonly_lpb", "readonly"].includes(f.type)) delete payload[f.key];
      // (jul-2026) Campos readonly informativos de la solicitud/producto: son SOLO
      // presentación (badges y bloques). NUNCA viajan en el payload; el backend hereda
      // del producto (comision_apertura_tipo, gastos_cobranza*) o de otras fuentes.
      if (["comision_apertura_tipo_readonly", "gastos_cobranza_readonly",
           "resumen_producto_credito", "producto_periodicidad_readonly"].includes(f.type)) {
        delete payload[f.key];
      }
      // Avisos (type="aviso") son texto de UI; nunca deben viajar en el payload.
      if (f.type === "aviso") delete payload[f.key];
      // captura_estatus es CALCULADO por el backend (prospectos): nunca se envía como captura.
      if (f.key === "captura_estatus") delete payload[f.key];
      // Año (INE): sanea a 4 dígitos y rango [2020, 2040]; fuera de rango → vacío (opcional).
      if (f.type === "year") {
        const s = String(payload[f.key] ?? "").replace(/\D+/g, "").slice(0, 4);
        const n = Number(s);
        payload[f.key] = (s.length === 4 && n >= 2020 && n <= 2040) ? s : "";
      }
    });
    // Build 1 · Factoraje: lo exclusivo de factoraje y el distintivo de deudor solo viajan si se ven.
    const _visibles = new Set(config.fields.filter(fieldVisible).map((f) => f.key));
    config.fields.forEach((f) => {
      if ((f.soloFactoraje || f.soloArrendamiento || f.soloSiVisible) && !_visibles.has(f.key)) delete payload[f.key];
    });
    // ── Auto-composición del `domicilio` legacy (jul-2026) ──────────────────
    // Si el operador dejó el textarea `domicilio` vacío PERO capturó al menos uno de
    // los campos estructurados, componemos automáticamente el legacy con el orden
    // exacto que espera SITI Anexo XVIII: [calle_y_numero, CP, colonia, municipio].
    // Esto preserva backward-compat para el EBR (que lee `domicilio` como texto) y
    // para REDECO/OT, sin sobrescribir ninguna captura manual del operador.
    const _domActual = String(payload.domicilio ?? "").trim();
    const _tieneEstructurado = ["calle_y_numero", "codigo_postal", "colonia", "municipio"]
      .some((k) => String(payload[k] ?? "").trim() !== "");
    if (!_domActual && _tieneEstructurado) {
      const _partes = [
        String(payload.calle_y_numero ?? "").trim(),
        String(payload.codigo_postal ?? "").trim() ? `C.P. ${String(payload.codigo_postal).trim()}` : "",
        String(payload.colonia ?? "").trim() ? `Col. ${String(payload.colonia).trim()}` : "",
        String(payload.municipio ?? "").trim(),
      ].filter(Boolean);
      payload.domicilio = _partes.join(", ");
    }
    try {
      if (editing) {
        await updateResource(config.name, editing.id, payload);
        toast.success("Registro actualizado");
      } else {
        const creado = await createResource(config.name, payload);
        toast.success("Registro creado");
        // Ubicación OPCIONAL del alta (un solo punto, sellado server-side). Nunca bloquea.
        if (config.geoCapture && geoPoint && creado?.id) {
          try {
            await api.post(config.geoCapture.endpoint(creado.id), geoPoint);
          } catch (e) { /* opcional: no bloquea el alta */ }
        }
        // Pantalla de resultado del cotejo PLD + EBR (informativa; reutiliza lo ya persistido,
        // sin reconsultar al proveedor). Sustituye al antiguo modal de carga de INE.
        if (config.screeningResultado && creado?.id) {
          setScreeningPid(creado.id);
        }
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await deleteResource(config.name, row.id);
      toast.success("Registro eliminado");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Fase 8+ · Feb-2026 — Fetch de conteos reales para el diálogo de "Cancelar cliente".
  // Estrictamente aditivo: usa endpoints existentes con filtro por cliente_id (list_items
  // genérico) o filtrado en cliente si el endpoint no acepta el parámetro. Nunca escribe.
  const cargarConteosCliente = async (cid) => {
    setCancelarConteos(null);
    // Estados terminales considerados "no vigentes" para créditos y líneas.
    const CRED_TERMINALES = new Set(["Liquidado", "Cancelado", "Castigado"]);
    const LIN_ACTIVAS = new Set(["Autorizada", "Vigente", "Suspendida"]);
    try {
      const [creditosRes, garantiasRes, expedientesRes, lineasRes] = await Promise.allSettled([
        api.get("/creditos", { params: { cliente_id: cid } }),
        api.get("/garantias", { params: { cliente_id: cid } }),
        api.get("/expedientes", { params: { cliente_id: cid } }),
        api.get("/lineas"),  // /lineas no acepta cliente_id: filtramos en cliente.
      ]);
      const creditos = Array.isArray(creditosRes.value?.data) ? creditosRes.value.data : [];
      const garantias = Array.isArray(garantiasRes.value?.data) ? garantiasRes.value.data : [];
      const expedientes = Array.isArray(expedientesRes.value?.data) ? expedientesRes.value.data : [];
      const lineasAll = Array.isArray(lineasRes.value?.data) ? lineasRes.value.data : [];
      const lineas = lineasAll.filter((l) => l.cliente_id === cid);
      // Pagos: se cuentan iterando créditos (el endpoint /pagos acepta credito_id, no cliente_id).
      let pagosCount = 0;
      if (creditos.length > 0) {
        const pagosRes = await Promise.allSettled(
          creditos.map((c) => api.get("/pagos", { params: { credito_id: c.id } }))
        );
        for (const pr of pagosRes) {
          if (pr.status === "fulfilled" && Array.isArray(pr.value?.data)) {
            pagosCount += pr.value.data.length;
          }
        }
      }
      const credVigentes = creditos.filter((c) => !CRED_TERMINALES.has(String(c.estatus || ""))).length;
      const linVigentes = lineas.filter((l) => LIN_ACTIVAS.has(String(l.estatus || ""))).length;
      setCancelarConteos({
        creditos: creditos.length,
        cred_vigentes: credVigentes,
        lineas: lineas.length,
        lin_vigentes: linVigentes,
        pagos: pagosCount,
        garantias: garantias.length,
        expedientes: expedientes.length,
        error: false,
      });
    } catch (_e) {
      setCancelarConteos({ creditos: 0, cred_vigentes: 0, lineas: 0, lin_vigentes: 0,
                          pagos: 0, garantias: 0, expedientes: 0, error: true });
    }
  };

  const abrirCancelarCliente = (row) => {
    setCancelarCliente({
      id: row.id,
      nombre: row.nombre || row.razon_social || "",
      rfc: row.rfc || "",
      estatus: row.estatus || "",
    });
    setCancelarConfirmTexto("");
    setCancelarEjecutando(false);
    cargarConteosCliente(row.id);
  };

  const ejecutarCancelarCliente = async () => {
    if (!cancelarCliente) return;
    setCancelarEjecutando(true);
    try {
      await deleteResource("clientes", cancelarCliente.id);
      toast.success("Cliente cancelado (baja lógica). El registro se conserva en la base para trazabilidad.");
      setCancelarCliente(null);
      setCancelarConteos(null);
      setCancelarConfirmTexto("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCancelarEjecutando(false);
    }
  };

  const onDeleteClick = (row) => {
    if (isEntidad) {
      setEliminarEntidad({ id: row.id, nombre: row.nombre_comercial || row.razon_social || row.nombre || "" });
    } else if (config.name === "clientes") {
      abrirCancelarCliente(row);
    } else {
      remove(row);
    }
  };

  const exportCsv = () => {
    const cols = config.columns;
    const header = cols.map((c) => c.label).join(",");
    const lines = filtered.map((r) =>
      cols.map((c) => `"${String(r[c.key] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${config.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sections = useMemo(() => {
    const map = {};
    config.fields.forEach((f) => {
      const s = f.section || "Datos";
      (map[s] = map[s] || []).push(f);
    });
    return map;
  }, [config.fields]);

  // ago-2026 · Fase 2 — Columna "Altas del mes" (consumo vs cupo del plan). Exclusiva de
  // super_admin en Entidades / Empresas: el endpoint responde 403 a cualquier otro rol, así
  // que un admin_entidad nunca puede ver el consumo de otras entidades.
  const columnas = useMemo(() => {
    if (!(isEntidad && user?.role === "super_admin")) return config.columns;
    return [...config.columns, {
      key: "_altas_mes",
      label: "Altas del mes",
      render: (row) => <CeldaAltasMes uso={altasUso?.items?.[row.id]} />,
    }];
  }, [config.columns, isEntidad, user?.role, altasUso]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="resource-search" placeholder="Buscar..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <div className="flex items-center gap-2">
          {extraToolbar}
          {/* Candado de productos por plan (jul-2026, iter_80). Chip informativo + estado
              del botón Nuevo. Solo se muestra para productos_credito y cuando la entidad
              tiene enforcement activo — grandfathered no ve nada (comportamiento legacy). */}
          {esProductosCredito && usoProductos?.enforcement && (
            <span
              data-testid="productos-uso-chip"
              className={
                "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium " +
                (usoProductos.unlimited
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : usoProductos.disponibles === 0
                  ? "border-red-200 bg-red-50 text-red-700"
                  : usoProductos.disponibles <= 2
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-slate-200 bg-slate-50 text-slate-700")
              }>
              {usoProductos.unlimited
                ? "Productos ilimitados"
                : `${usoProductos.activos}/${usoProductos.limite_efectivo} productos activos`}
            </span>
          )}
          {canExport && ["clientes", "creditos", "cobranza"].includes(config.name) && (
            <ExportExcelButton endpoint={`/exportar/gestion/${config.name}`} filename={`${config.name}.xlsx`}
              label="Excel" testid="export-xlsx-btn" />
          )}
          {canExport && (
            <TooltipButton tooltip="Descargar la tabla en formato CSV (Excel)" data-testid="export-csv-btn" variant="outline" size="sm" onClick={exportCsv}>
              <Download className="mr-1.5 h-4 w-4" /> CSV
            </TooltipButton>
          )}
          {canCreate && (() => {
            // feb-2026 · iter 08-01 — Concepto Actividad Vulnerable (LFPIORPI): en el recurso
            // `clientes` NO se ofrece el botón "Nuevo". Los clientes deben entrar por
            // Prospectos, donde corre el screening y el EBR. En SOFOM/CNBV el botón sigue
            // visible (no se toca).
            const esClientesAV = config.name === "clientes" && esActividadVulnerable(user?.giro);
            if (esClientesAV) return null;
            // Gating del botón Nuevo cuando ya alcanzaste el límite del plan. El mensaje
            // completo (con WhatsApp) llega si se intenta crear igualmente por API.
            const bloqueado = esProductosCredito && usoProductos?.enforcement &&
              !usoProductos.unlimited && usoProductos.disponibles === 0;
            const btn = (
              <Button data-testid="new-record-btn" size="sm" onClick={openNew}
                disabled={bloqueado}
                className="bg-primary hover:bg-sidebar-hover disabled:opacity-60">
                <Plus className="mr-1.5 h-4 w-4" /> Nuevo
              </Button>
            );
            if (!bloqueado) return btn;
            return (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span data-testid="new-record-btn-lock" tabIndex={0}>{btn}</span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs text-left">
                    Alcanzaste el límite de {usoProductos.limite_efectivo} productos de tu plan.
                    Para agregar más, contáctanos por WhatsApp: +52 222 486 0318.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })()}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500">
        <span>{loading ? "Cargando..." : `${filtered.length} registro(s)`}</span>
      </div>

      {/* feb-2026 · iter 08-01 — Concepto Actividad Vulnerable: aviso explicativo cuando el
          botón Nuevo se oculta (en `clientes` para giros LFPIORPI). En SOFOM/CNBV NO aparece. */}
      {config.name === "clientes" && esActividadVulnerable(user?.giro) && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800"
             data-testid="clientes-av-aviso">
          <b>¿Dónde doy de alta un cliente?</b> Los clientes de tu actividad vulnerable
          entran <b>por Prospectos</b> (menú lateral). Al darlos de alta ahí, el sistema
          corre automáticamente el screening contra listas oficiales (SAT 69, OFAC, ONU,
          LPB, PEP) y la evaluación EBR — como lo exige la LFPIORPI. Cuando el prospecto
          queda "Calificado", se convierte en cliente y aparece en esta pantalla.
        </div>
      )}
      <DataTable
        testid={`table-${config.name}`}
        columns={columnas}
        rows={filtered}
        onRowClick={canView ? goView : undefined}
        emptyLabel="Aún no hay registros. Crea el primero con “Nuevo”."
        actions={
          canView || canEdit || canDelete || extraRowActions
            ? (row) => (
                <div className="flex justify-end gap-1">
                  {extraRowActions && extraRowActions(row, { reload: load })}
                  {/* sep-2026 · Ajuste manual del cupo mensual de altas (solo super_admin).
                      Reusa el motor de altas del plan (`plan_creditos`), no lo duplica. */}
                  {isEntidad && user?.role === "super_admin" && (
                    <AjustarAltasButton entidad={row} uso={altasUso?.items?.[row.id]}
                      onDone={cargarAltasUso} />
                  )}
                  {/* sep-2026 · Solicitudes: envío al Comité (= sellado de condiciones) y
                      cancelación con motivo. Sustituyen al bote de basura: una solicitud
                      nunca se borra, se cancela y queda como evidencia. */}
                  {config.name === "solicitudes" && (
                    <AccionesSolicitud solicitud={row} onDone={load} />
                  )}
                  {canView && (
                    <Button data-testid={`view-${row.id}`} variant="outline" size="sm" className="h-8 gap-1.5 px-2.5 text-primary"
                      onClick={() => goView(row)}>
                      <Eye className="h-4 w-4" /> Ver
                    </Button>
                  )}
                  {/* feb-2026 · iter 07-26 — Atajo a la ficha 360 del cliente desde la lista.
                      ago-2026 · La ruta la DECLARA el recurso (`config.ficha360Path`), ya no se
                      deduce de `config.name`: `clientes_av` comparte `name: "clientes"` para
                      reusar endpoint y RBAC, así que heredaba este botón hacia `/clientes-360`
                      —pantalla del vertical SOFOM— y la entidad de actividad vulnerable recibía
                      "Módulo de otro régimen". */}
                  {config.ficha360Path && (
                    <TooltipButton tooltip="Ver ficha 360 del cliente" data-testid={`view-ficha-${row.id}`}
                      variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => navigate(config.ficha360Path.includes(":id")
                        ? config.ficha360Path.replace(":id", row.id)
                        : `${config.ficha360Path}?id=${row.id}`)}>
                      <UserSearch className="h-4 w-4 text-primary" />
                    </TooltipButton>
                  )}
                  {canEdit && (!config.isRowEditable || config.isRowEditable(row)) && (
                    <TooltipButton tooltip="Editar este registro" data-testid={`edit-${row.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}>
                      <Pencil className="h-4 w-4 text-slate-500" />
                    </TooltipButton>
                  )}
                  {canDelete && (
                    <TooltipButton
                      tooltip={config.name === "clientes" ? "Cancelar cliente (baja lógica)" : "Eliminar este registro"}
                      data-testid={`delete-${row.id}`} variant="ghost" size="icon" className="h-8 w-8"
                      onClick={() => onDeleteClick(row)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </TooltipButton>
                  )}
                </div>
              )
            : undefined
        }
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar" : "Nuevo"} — {config.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-1">
            {config.formNotice && (
              <div data-testid="form-notice" className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p>{config.formNotice}</p>
              </div>
            )}
            {fideicomisoBloqueado && (
              <div
                data-testid="fideicomiso-bloqueo-notice"
                className="flex gap-2 rounded-md border-2 border-amber-400 bg-amber-100 p-3 text-xs text-amber-900"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <p>
                  <span className="font-semibold">Los fideicomisos se dan de alta en el módulo de Fideicomisos.</span>{" "}
                  Este formulario no los captura. Selecciona otro tipo de persona para continuar, o abre el módulo dedicado a Fideicomisos.
                </p>
              </div>
            )}
            {config.name === "solicitudes" && editing && (
              <div data-testid="solicitud-etapa-panel"
                className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="text-slate-500">Etapa del proceso:</span>
                  <span data-testid="solicitud-etapa">
                    <StatusBadge value={etiquetaEstatus(estatusEfectivo(editing))} />
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-slate-500">
                  Ejecutivo responsable:
                  <b data-testid="solicitud-ejecutivo" className="text-slate-800">
                    {editing.ejecutivo || editing.created_by || "—"}
                  </b>
                </span>
                <span className="text-[10px] text-slate-400">
                  Automáticos: el estatus lo mueven las acciones del flujo y el responsable es
                  quien capturó la solicitud.
                </span>
              </div>
            )}
            {config.name === "solicitudes" && editing && (
              <div data-testid="solicitud-sello-panel"
                className={"rounded-md border px-3 py-2.5 text-xs " + (estaCancelada(editing)
                  ? "border-red-200 bg-red-50 text-red-800"
                  : estaSellada(editing)
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-slate-50 text-slate-600")}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="max-w-xl">
                    {estaCancelada(editing) ? (
                      <>Solicitud <b>cancelada</b>{editing.cancelado_motivo ? <> — {editing.cancelado_motivo}</> : null}.
                        {" "}Se conserva como evidencia y sus datos ya no se editan.</>
                    ) : estaSellada(editing) ? (
                      <>Condiciones <b>selladas</b> al enviarla al Comité
                        {editing.sello?.enviada_por ? <> por {editing.sello.enviada_por}</> : null}. Para
                        cambiar montos o condiciones, cancela esta solicitud y captura una nueva.
                        Siguen abiertos: documentos, consulta SIC, comentarios, cuenta y medio de
                        dispersión, garantía descriptiva y obligados solidarios.</>
                    ) : (
                      <>Al pulsar <b>«Enviar al Comité»</b> quedan selladas cliente, producto, montos,
                        plazo, periodicidad, tasas, comisión e IVA. El Comité aprueba o rechaza; no
                        modifica condiciones.</>
                    )}
                  </p>
                  <span className="flex shrink-0 items-center gap-2">
                    <AccionesSolicitud solicitud={editing}
                      onDone={() => { setOpen(false); load(); }} />
                  </span>
                </div>
              </div>
            )}
            {editing && extraEditContent && extraEditContent(editing, { reload: load })}
            {Object.entries(sections).map(([section, fields]) => {
              const visibles = fields.filter(fieldVisible);
              if (!visibles.length) return null;
              return (
              <div key={section}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">{section}</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibles.map((f, _fi) => (
                    f.type === "aviso" ? (
                      <div key={`${f.key}-${_fi}`} data-testid={`aviso-${f.key}`}
                        className="sm:col-span-2 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        <p>{f.text}</p>
                      </div>
                    ) : (
                    <div key={`${f.key}-${_fi}`} className={f.full ? "sm:col-span-2" : ""}>
                      <Label htmlFor={`field-${f.key}`} className="mb-1 flex items-center gap-1 text-xs font-medium text-slate-600">
                        <span>{f.label}{(f.required || f.requiredCreateOnly) && <span className="ml-0.5 text-red-500">*</span>}</span>
                        {f.help && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" tabIndex={-1} data-testid={`help-${f.key}`}
                                  className="inline-flex text-slate-400 hover:text-primary" aria-label={`Ayuda: ${f.label}`}>
                                  <Info className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-left">{f.help}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </Label>
                      {camposSellados.includes(f.key) ? (
                        <div data-testid={`campo-sellado-${f.key}`}
                          title="Sellado al enviar la solicitud al Comité — para cambiarlo, cancela y captura una nueva"
                          className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-600">
                          <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="truncate">{textoSellado(f, form)}</span>
                        </div>
                      ) : (
                      <FieldControl field={f} value={form[f.key === "producto_id" ? "producto_id" : f.key]} form={form} onChange={onChange} productos={productos} onProducto={onProducto} clientes={clientes} onCliente={onCliente} creditos={creditos} onCredito={onCredito} onCreditoQuery={buscarCreditosBackend} paises={paises} />
                      )}
                    </div>
                    )
                  ))}
                </div>
              </div>
              );
            })}
            {/* Pagos (recurso genérico) — tabla de amortización SELECCIONABLE del crédito
                elegido (jul-2026). Mismo componente que Cartera (TablaAmortizacionCredito):
                ambas vistas quedan visualmente alineadas. Clic en una cuota = pre-llenar su
                pendiente sellado. */}
            {config.name === "pagos" && !editing && pagoCredito?.tabla_amortizacion?.length > 0 && (
              <div data-testid="pago-tabla-section">
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                    {esCreditoArrendamiento(pagoCredito) ? TITULO_RENTAS : "Tabla de amortización"}{pagoCredito.numero_credito ? ` — ${pagoCredito.numero_credito}` : ""}
                  </p>
                  <span className="text-[10.5px] text-slate-500">
                    Clic en una cuota para pre-llenar su pendiente
                  </span>
                </div>
                <TablaPago
                  tabla={pagoCredito.tabla_amortizacion}
                  cargosGc={pagoCredito.cargos_cobranza || []}
                  gcEtiqueta={pagoCredito.gc_etiqueta || "Gastos de cobranza"}
                  selectable
                  selectedNo={pagoCuotaSel}
                  onSelectCuota={onSelectCuota}
                  testId="pago-tabla-amortizacion"
                  resaltarTotal={pagoCredito.tipo_amortizacion === "francesa_total_fijo"}
                />
              </div>
            )}
            {/* Pagos (recurso genérico) — desglose ORDENADO SEGÚN PRELACIÓN DEL MOTOR
                (feb-2026 iter123). Reemplaza el desglose anterior (que no respetaba el
                orden real). El motor cobra: 1) mora diferida + IVA → 2) GC diferidos +
                IVA → 3) GC activos + IVA → 4) comisión + IVA → 5) interés + IVA →
                6) capital → 7) mora corriente + IVA. La suma cuadra al centavo con el
                TOTAL EXIGIBLE que la tabla muestra en la columna "Total exigible". */}
            {config.name === "pagos" && !editing && pagoSugerido && (pagoSugerido.totalPrelacion || 0) > 0 && (
              <div data-testid="pago-sugerido-desglose"
                className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2.5 text-xs text-sky-900">
                <p className="mb-1.5 font-semibold">
                  Total exigible del crédito (orden de aplicación del motor)
                  {pagoSugerido.proximaCuotaNo ? (
                    <span className="font-normal">
                      {" "}(cuota #{pagoSugerido.proximaCuotaNo}
                      {pagoSugerido.fecha ? `, vence ${fdate(pagoSugerido.fecha)}` : ""})
                    </span>
                  ) : null}:
                </p>
                <div className="space-y-0.5 font-mono tabular-nums">
                  {(pagoSugerido.desglosePrelacion || []).map((it, idx) => (
                    <div key={`prel-${it.tipo}-${it.cuota_no ?? "cred"}-${idx}`}
                      className="flex justify-between"
                      data-testid={`pago-prelacion-${it.tipo}`}>
                      <span>
                        <span className="mr-1.5 inline-block w-4 text-right text-[10px] font-sans text-sky-700/70">{idx + 1}.</span>
                        {it.label}
                      </span>
                      <span>{mxn(it.monto)}</span>
                    </div>
                  ))}
                  <div className="mt-1 flex justify-between border-t border-sky-200 pt-1 font-semibold"
                    data-testid="pago-sugerido-total">
                    <span>Total exigible</span>
                    <span>{mxn(pagoSugerido.totalPrelacion || pagoSugerido.total)}</span>
                  </div>
                </div>
                <p className="mt-1.5 text-[10.5px] text-sky-800">
                  Orden real de cobro del motor sellado (pagos_engine). El operador puede
                  pagar de menos o de más — un monto MENOR deja los conceptos finales
                  pendientes; un MAYOR queda como saldo a favor.
                </p>
              </div>
            )}
            {/* iter124 (caso ZINALI CR-2026-006) — Desglose EXIGIBLE POR INTEGRANTE
                para créditos GRUPALES. El motor por integrante se conserva; sólo cambia
                el reparto: proporcional al EXIGIBLE (no al saldo insoluto). El operador
                ve cuánto le toca a cada quien. */}
            {config.name === "pagos" && !editing && pagoExigiblePorIntegrante.length > 0 && (
              <div data-testid="pago-exigible-por-integrante"
                className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 text-xs text-indigo-900">
                <p className="mb-1.5 font-semibold">
                  Exigible por integrante (crédito grupal)
                </p>
                <div className="space-y-1 font-mono tabular-nums">
                  {pagoExigiblePorIntegrante.map((ex, i) => (
                    <div key={`ex-int-${ex.cliente_id || i}`}
                      className="rounded border border-indigo-200/70 bg-white px-2 py-1"
                      data-testid={`pago-exigible-integrante-${ex.cliente_id}`}>
                      <div className="flex justify-between font-semibold">
                        <span className="text-[11.5px]">
                          {ex.cliente}
                          {ex.rol === "titular" && (
                            <span className="ml-1 rounded bg-indigo-600 px-1 py-0.5 text-[9px] text-white font-sans">TITULAR</span>
                          )}
                        </span>
                        <span>{mxn(ex.exigible_total)}</span>
                      </div>
                      {(ex.gc_activo > 0.005 || ex.gc_diferido > 0.005
                        || ex.mora_diferida > 0.005 || ex.subtotal_cuotas > 0.005) && (
                        <div className="mt-0.5 space-y-0.5 pl-2 text-[10.5px] font-sans text-indigo-800">
                          {ex.mora_diferida > 0.005 && (
                            <div className="flex justify-between font-mono"><span>· Mora congelada diferida (+IVA)</span><span>{mxn(ex.mora_diferida + ex.iva_mora_diferida)}</span></div>
                          )}
                          {(ex.gc_diferido > 0.005 || ex.iva_gc_diferido > 0.005) && (
                            <div className="flex justify-between font-mono"><span>· GC diferidos (+IVA)</span><span>{mxn(ex.gc_diferido + ex.iva_gc_diferido)}</span></div>
                          )}
                          {(ex.gc_activo > 0.005 || ex.iva_gc_activo > 0.005) && (
                            <div className="flex justify-between font-mono"><span>· GC activos (+IVA)</span><span>{mxn(ex.gc_activo + ex.iva_gc_activo)}</span></div>
                          )}
                          {ex.cuotas_del_periodo.map((cu) => (
                            <div key={`cu-${ex.cliente_id}-${cu.no}`} className="flex justify-between font-mono">
                              <span>· Cuota #{cu.no} ({cu.mora_corriente > 0.005 ? "vencida" : "próxima"})</span>
                              <span>{mxn(cu.subtotal)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <div className="mt-1 flex justify-between border-t border-indigo-300 pt-1 font-semibold" data-testid="pago-exigible-grupo-total">
                    <span>Total exigible del grupo</span>
                    <span>{mxn(pagoExigiblePorIntegrante.reduce((s, x) => s + Number(x.exigible_total || 0), 0))}</span>
                  </div>
                </div>
                <p className="mt-1.5 text-[10.5px] text-indigo-800">
                  El motor reparte el pago proporcional al exigible de cada integrante.
                  Si pagas exactamente el total, cada uno recibe su exigible completo,
                  NADA se adelanta a cuotas futuras. Los centavos de redondeo cuelgan del titular.
                </p>
              </div>
            )}
            {/* feb-2026 iter123 — Aviso preventivo: monto capturado vs total exigible.
                Se pinta cuando el operador captura un monto distinto al sugerido.
                No bloquea el guardado; sólo informa lo que va a pasar. */}
            {config.name === "pagos" && !editing && pagoSugerido && (pagoSugerido.totalPrelacion || 0) > 0 && (() => {
              const totalEx = Number(pagoSugerido.totalPrelacion || pagoSugerido.total || 0);
              const monto = Number(String(form.monto || "0").replace(/[^0-9.-]/g, "")) || 0;
              if (monto <= 0.005) return null;
              const diff = Math.round((monto - totalEx) * 100) / 100;
              if (Math.abs(diff) < 0.005) {
                return (
                  <div data-testid="pago-monto-status-exacto"
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    <b>Monto exacto.</b> Cubre el total exigible y deja la cuota #{pagoSugerido.proximaCuotaNo}
                    {" "}en cero.
                  </div>
                );
              }
              if (diff > 0) {
                return (
                  <div data-testid="pago-monto-status-mayor"
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    <b>Cubre el total exigible ({mxn(totalEx)}).</b> Sobran <b>{mxn(diff)}</b> — quedarán
                    como saldo a favor del crédito.
                  </div>
                );
              }
              // monto < totalEx  →  advertencia de pago parcial. Simulamos el orden
              // del motor para decir "hasta dónde alcanza": iteramos el desglose
              // ordenado y descontamos el monto hasta agotarlo.
              let restante = monto;
              const cubiertos = [];
              const parcial = [];
              const pendientes = [];
              (pagoSugerido.desglosePrelacion || []).forEach((it) => {
                if (restante <= 0.005) { pendientes.push(it); return; }
                if (restante + 0.005 >= it.monto) {
                  cubiertos.push(it);
                  restante = Math.round((restante - it.monto) * 100) / 100;
                } else {
                  parcial.push({ ...it, aplicado: Math.round(restante * 100) / 100,
                                 restante_concepto: Math.round((it.monto - restante) * 100) / 100 });
                  restante = 0;
                }
              });
              const faltante = Math.round((totalEx - monto) * 100) / 100;
              return (
                <div data-testid="pago-monto-status-menor"
                  className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="font-semibold">
                    Pago PARCIAL — quedará pendiente <b>{mxn(faltante)}</b> tras aplicar {mxn(monto)}.
                  </p>
                  <p className="mt-1 text-[11px] text-amber-800">
                    El motor consumirá el monto en este orden hasta agotarlo:
                  </p>
                  <ul className="mt-1 list-none space-y-0.5 pl-3 text-[11px] font-mono tabular-nums">
                    {cubiertos.map((it, i) => (
                      <li key={`cub-${it.tipo}-${i}`} className="text-emerald-800" data-testid={`pago-parcial-cubre-${it.tipo}`}>
                        ✓ {it.label}: {mxn(it.monto)}
                      </li>
                    ))}
                    {parcial.map((it, i) => (
                      <li key={`par-${it.tipo}-${i}`} className="text-amber-800" data-testid={`pago-parcial-corta-${it.tipo}`}>
                        ◐ {it.label}: aplica {mxn(it.aplicado)} — queda {mxn(it.restante_concepto)}
                      </li>
                    ))}
                    {pendientes.map((it, i) => (
                      <li key={`pen-${it.tipo}-${i}`} className="text-rose-700" data-testid={`pago-parcial-sin-cubrir-${it.tipo}`}>
                        ✗ {it.label}: {mxn(it.monto)} — sin cubrir
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-[10.5px] text-amber-800">
                    Puedes aplicar el pago parcial y registrar el resto después.
                  </p>
                </div>
              );
            })()}
            {/* Aviso preventivo del candado de productos (jul-2026, iter_80). Solo aplica al
                recurso productos_credito, al alta nueva y cuando quedan pocos espacios. */}
            {esProductosCredito && !editing && usoProductos?.enforcement && !usoProductos.unlimited
              && usoProductos.disponibles > 0 && usoProductos.disponibles <= 2 && (
              <div data-testid="productos-aviso-preventivo"
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                Solo puedes agregar <b>{usoProductos.disponibles}</b> producto(s) más con tu
                plan actual ({usoProductos.activos}/{usoProductos.limite_efectivo}). Cuando
                llegues al tope, contáctanos por WhatsApp para ampliar el cupo.
              </div>
            )}
            {config.sicConsentimiento && (
              <div data-testid="sic-consentimiento-section">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Autorización de Buró/Círculo (SIC)</p>
                {form.cliente_id ? (
                  <SicAutorizacionProspecto
                    key={`sic-${form.cliente_id}`}
                    idField={config.sicConsentimiento.idField || "cliente_id"}
                    recordId={form.cliente_id}
                    sujeto="cliente"
                    showConsultar={config.sicConsentimiento.showConsultar !== false} />
                ) : (
                  <p data-testid="sic-sin-cliente" className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                    Selecciona primero al cliente para registrar la autorización de consulta a Buró/Círculo.
                  </p>
                )}
              </div>
            )}
            {config.docsIdentidad && (
              <div data-testid="docs-identidad-section">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Documentos de identidad</p>
                {form.cliente_id ? (
                  <>
                    <p className="mb-2 text-[11px] text-slate-500">
                      Cada archivo se sube y cifra automáticamente al seleccionarlo: queda en el expediente del cliente y aparece en su Perfil 360 aunque cierres sin guardar. No son obligatorios para guardar la solicitud.
                    </p>
                    <DocsIdentidadCliente clienteId={form.cliente_id} docs={config.docsIdentidad.docs.filter((d) => !d.soloEstatus || d.soloEstatus.includes(form.estatus))}
                      referenciaId={form.id || ""} />
                  </>
                ) : (
                  <p data-testid="docs-sin-cliente" className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                    Selecciona primero al cliente para cargar los documentos de identidad.
                  </p>
                )}
              </div>
            )}
            {config.geoCapture && (
              <div data-testid="geo-capture-section">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Ubicación</p>
                <GeoPuntoOpcional
                  testidPrefix="geo-solicitud"
                  label={config.geoCapture.label || "Confirmar ubicación (opcional)"}
                  microtext={config.geoCapture.microtext}
                  value={geoPoint}
                  saved={editing ? editing[config.geoCapture.field] : null}
                  onCapture={setGeoPoint}
                  onClear={() => setGeoPoint(null)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-record-btn" onClick={save} disabled={saving || fideicomisoBloqueado} className="bg-primary hover:bg-sidebar-hover">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {config.screeningResultado && (
        <ScreeningResultadoDialog
          prospectoId={screeningPid}
          open={!!screeningPid}
          onOpenChange={(v) => { if (!v) setScreeningPid(null); }}
        />
      )}

      {isEntidad && (
        <EliminarEntidadDialog
          open={!!eliminarEntidad}
          onOpenChange={(v) => { if (!v) setEliminarEntidad(null); }}
          entityId={eliminarEntidad?.id}
          nombre={eliminarEntidad?.nombre}
          onDone={load}
        />
      )}

      {/* Fase 8+ · Feb-2026 — Diálogo de "Cancelar cliente" (baja lógica) con conteos reales.
          Se muestra SOLO cuando config.name === "clientes". El backend ya hace baja lógica
          (DELETE /api/clientes/{id}) — este diálogo agrega visibilidad y confirmación con
          fricción proporcional al riesgo: si hay créditos o líneas VIGENTES se exige tipear
          "CANCELAR" para desbloquear el botón. Si no hay operación vigente, un click basta. */}
      {config.name === "clientes" && (() => {
        const c = cancelarConteos;
        const cargando = cancelarCliente && c == null;
        const hayVigentes = !!c && (c.cred_vigentes > 0 || c.lin_vigentes > 0);
        const requiereTipeo = hayVigentes;
        const tipeoValido = !requiereTipeo || cancelarConfirmTexto.trim().toUpperCase() === "CANCELAR";
        const botonHabilitado = !!c && !cargando && !cancelarEjecutando && tipeoValido;
        return (
          <AlertDialog
            open={!!cancelarCliente}
            onOpenChange={(v) => {
              if (!v && !cancelarEjecutando) {
                setCancelarCliente(null);
                setCancelarConteos(null);
                setCancelarConfirmTexto("");
              }
            }}
          >
            <AlertDialogContent data-testid="cancelar-cliente-dialog" className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="cancelar-cliente-title" className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Cancelar cliente
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm text-slate-700">
                    <p>
                      Vas a dar de baja al cliente{" "}
                      <span className="font-semibold text-slate-900" data-testid="cancelar-cliente-nombre">
                        {cancelarCliente?.nombre || "(sin nombre)"}
                      </span>
                      {cancelarCliente?.rfc && (
                        <> · <span className="font-mono text-xs text-slate-600">{cancelarCliente.rfc}</span></>
                      )}
                      .
                    </p>
                    <p className="text-xs text-slate-500">
                      Esta es una <b>baja lógica</b>. El registro y su historial se conservan en la base para
                      trazabilidad (51ª LFPIORPI); nada se borra físicamente. Sus créditos, líneas, pagos y
                      expedientes permanecen intactos para auditoría.
                    </p>

                    {cargando && (
                      <div data-testid="cancelar-cliente-cargando"
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                        Consultando operación del cliente…
                      </div>
                    )}

                    {c && (
                      <div data-testid="cancelar-cliente-conteos"
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <p className="mb-1.5 font-semibold uppercase tracking-wide text-slate-500">Operación del cliente</p>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <li data-testid="cancelar-cliente-creditos">
                            Créditos: <b>{c.creditos}</b>
                            {c.cred_vigentes > 0 && (
                              <span className="ml-1 text-red-700">({c.cred_vigentes} vigentes)</span>
                            )}
                          </li>
                          <li data-testid="cancelar-cliente-lineas">
                            Líneas: <b>{c.lineas}</b>
                            {c.lin_vigentes > 0 && (
                              <span className="ml-1 text-red-700">({c.lin_vigentes} activas)</span>
                            )}
                          </li>
                          <li data-testid="cancelar-cliente-pagos">Pagos: <b>{c.pagos}</b></li>
                          <li data-testid="cancelar-cliente-garantias">Garantías: <b>{c.garantias}</b></li>
                          <li data-testid="cancelar-cliente-expedientes">Expedientes: <b>{c.expedientes}</b></li>
                        </ul>
                        {c.error && (
                          <p className="mt-1.5 text-[11px] text-amber-700">
                            Algunas cifras no pudieron consultarse; procede con precaución.
                          </p>
                        )}
                      </div>
                    )}

                    {hayVigentes && (
                      <div data-testid="cancelar-cliente-advertencia"
                        className="rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 text-xs text-red-900">
                        <p className="mb-1 flex items-center gap-1.5 font-semibold">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Este cliente tiene operación vigente.
                        </p>
                        <p>
                          Cancelarlo NO cancela sus créditos ni líneas: esos flujos tienen sus propias
                          reglas de baja. Confirma solo si tienes la instrucción operativa de dar de baja
                          al cliente aunque su cartera siga activa.
                        </p>
                        <div className="mt-2">
                          <Label htmlFor="cancelar-cliente-confirm-input"
                            className="mb-1 block text-[11px] font-medium text-red-900">
                            Escribe <b>CANCELAR</b> para desbloquear:
                          </Label>
                          <Input
                            id="cancelar-cliente-confirm-input"
                            data-testid="cancelar-cliente-confirm-input"
                            autoComplete="off"
                            value={cancelarConfirmTexto}
                            onChange={(e) => setCancelarConfirmTexto(e.target.value)}
                            className="h-8 border-red-300 bg-white text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancelar-cliente-cancel-btn" disabled={cancelarEjecutando}>
                  No, mantener cliente
                </AlertDialogCancel>
                <AlertDialogAction
                  data-testid="cancelar-cliente-confirm-btn"
                  disabled={!botonHabilitado}
                  onClick={(e) => { e.preventDefault(); ejecutarCancelarCliente(); }}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600 disabled:opacity-50"
                >
                  {cancelarEjecutando ? "Cancelando…" : "Sí, cancelar cliente"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        );
      })()}
    </div>
  );
}
